/*
 * ПРОГРАММА КУРСОВ «КУБИКОН CAD».
 *
 * Уроки описаны данными, а не кодом: у каждого — теория, шаги практики
 * и критерии автопроверки (см. checker.js). Такой формат позволяет
 * добавлять уроки, не трогая ни панель, ни проверку.
 *
 * ПОЧЕМУ УРОКИ ЗДЕСЬ, А НЕ В БАЗЕ. Содержание меняется вместе с редактором:
 * критерий ссылается на возможности ядра, шаг — на название кнопки. Если
 * держать уроки в базе, они разъедутся с интерфейсом при первом же
 * переименовании. В коде они правятся и выкатываются одним куском.
 * В базе живёт только прогресс ученика — он к интерфейсу не привязан.
 *
 * ФОРМАТ УРОКА:
 *   id       — устойчивый ключ, по нему хранится прогресс. НЕ переименовывать.
 *   title    — как видит ребёнок
 *   goal     — одна фраза: что он получит в конце
 *   theory   — короткий разбор перед практикой (абзацы)
 *   demos    — анимации к теории: [{ kind, …параметры }] из CadDemos.jsx
 *   blueprint— чертёж в трёх проекциях (см. Blueprint.jsx), если он нужен
 *   steps    — шаги практики: что делать. Отмечаются вручную, это ориентир
 *   task     — задание целиком, его и проверяем
 *   criteria — критерии автопроверки (kind из checker.js)
 *   hints    — три уровня помощи: подсказка → наводящий вопрос → разбор
 */

/** Курс 1. «Первые детали» — знакомство с редактором. */
const COURSE_FIRST_PARTS = {
    id: 'first-parts',
    title: 'Первые детали',
    subtitle: 'Знакомство с редактором',
    description: 'Научимся двигаться в трёхмерном пространстве, строить простые '
        + 'тела и понимать, из чего вообще состоит деталь.',
    lessons: [

        {
            id: 'fp-01-space',
            title: 'Пространство и виды',
            goal: 'Свободно крутить сцену и построить первый брусок.',
            theory: [
                'Вот окно редактора. Сверху — панель инструментов: слева зелёная '
                + 'кнопка «Эскиз», в середине синие «Выдавить» и «Отверстие», '
                + 'справа жёлтые «Скругление» и «Оболочка». Всё большое поле '
                + 'под панелью — это сцена, там живёт твоя деталь.',
                'Деталь стоит в трёхмерном пространстве, и разглядывать её надо '
                + 'со всех сторон — как игрушку в руках. Зажми правую кнопку '
                + 'мыши и веди в сторону: сцена повернётся вокруг детали. '
                + 'Колёсико приближает и отдаляет.',
                'Если нужен ровный вид — не крути мышью, а щёлкни по грани '
                + 'куба-навигатора в правом верхнем углу сцены. Камера сразу '
                + 'встанет точно спереди, сверху или сбоку.',
                'Три цветные линии в центре — это оси, направления в '
                + 'пространстве. Красная ось X, зелёная Y, синяя Z. Z смотрит '
                + 'вверх: по ней растёт высота детали.',
                'Любая деталь начинается с плоской фигуры — эскиза, который '
                + 'потом поднимают в объём. Этим и займёмся в задании: нарисуем '
                + 'прямоугольник и вытянем его в брусок.',
            ],
            demos: [
                { kind: 'ui-overview' },
                { kind: 'ui-orbit' },
                { kind: 'ui-viewcube' },
                { kind: 'views' },
                { kind: 'ui-extrude' },
            ],
            steps: [
                'Покрути сцену правой кнопкой мыши',
                'Щёлкни по грани куба-навигатора — посмотри вид сверху',
                'Вернись к объёмному виду (угол куба)',
                'Построй брусок 40 × 30 × 20 мм',
            ],
            task: 'Построй прямоугольный брусок 40 × 30 × 20 мм: нарисуй в эскизе '
                + 'прямоугольник 40 на 30 мм и выдави его на 20 мм в высоту. '
                + 'На какой плоскости начинать — неважно, проверяется сама деталь.',
            blueprint: {
                front: {
                    w: 40,
                    h: 20,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 40, h: 20 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 28, x2: 40, y2: 28, from: 20, label: '40' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 20, from: 0, label: '20' },
                    ]
                },
                side: {
                    w: 30,
                    h: 20,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 30, h: 20 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 28, x2: 30, y2: 28, from: 20, label: '30' },
                    ]
                },
                top: {
                    w: 40,
                    h: 30,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 40, h: 30 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 38, x2: 40, y2: 38, from: 30, label: '40' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 30, from: 0, label: '30' },
                    ]
                },
                note: 'Простой брусок. Три вида одной и той же детали: спереди '
                    + 'видно ширину и высоту, слева — глубину, сверху — ширину и '
                    + 'глубину.'
            },
            criteria: [
                { kind: 'size', value: [40, 30, 20], tolerance: 0.5, label: 'Габариты бруска' },
                { kind: 'bodyCount', value: 1, label: 'Одно тело' },
            ],
            hints: [
                'Брусок делается в два приёма: сначала плоский прямоугольник, '
                + 'потом ему задают высоту.',
                'Прямоугольник 40 × 30 — это эскиз. Какой инструмент превращает '
                + 'плоскую фигуру в объёмную?',
                'Возьми «Эскиз», выбери плоскость XY, нарисуй прямоугольник и '
                + 'поставь размеры 40 и 30. Закрой эскиз, выбери «Выдавить», '
                + 'укажи 20 мм.',
            ],
        },

        {
            id: 'fp-02-sketch',
            title: 'Эскиз и размеры',
            goal: 'Рисовать эскизы с точными размерами, а не на глаз.',
            theory: [
                'Эскиз — плоский чертёж будущей детали. Почти всё в редакторе '
                + 'начинается с него, поэтому разберёмся, как его создать.',
                'Нажми зелёную кнопку «Эскиз» — самую левую на панели. Редактор '
                + 'спросит, на какой плоскости рисовать: щёлкни по нужной прямо '
                + 'в сцене, и она станет твоим листом бумаги.',
                'Теперь выбери фигуру — например, прямоугольник — и протяни её '
                + 'мышью. Пока это только набросок: размеры примерные.',
                'Главное правило: рисуй примерно, а размер задавай числом. '
                + 'Впиши в поле точное значение — и фигура станет ровно такой. '
                + 'Если оставить «на глаз», эскиз поедет при первой же правке: '
                + 'потянешь одну линию, а сдвинется половина рисунка.',
                'Когда все размеры проставлены, эскиз закреплён — случайно мышью '
                + 'его уже не сдвинуть. Закрывай эскиз и переходи к объёму.',
            ],
            demos: [
                { kind: 'ui-sketch' },
                { kind: 'ui-rect' },
                { kind: 'dimensions' },
                { kind: 'extrude', shape: 'circle' },
            ],
            steps: [
                'Создай эскиз на плоскости XY',
                'Нарисуй прямоугольник примерно нужного размера',
                'Проставь размеры: 50 и 30 мм',
                'Нарисуй окружность внутри и задай диаметр 20 мм',
                'Закрой эскиз и выдави на 10 мм',
            ],
            task: 'Построй пластину 50 × 30 × 10 мм со сквозным отверстием '
                + 'Ø20 мм посередине: прямоугольник 50 на 30 мм в эскизе, '
                + 'выдавить на 10 мм.',
            // Первый чертёж курса. Деталь простая нарочно: задача не в том,
            // чтобы её построить, а в том, чтобы прочитать три вида.
            blueprint: {
                front: {
                    w: 50, h: 10,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 50, h: 10 },
                        // Отверстие в этом виде не видно — штриховые линии.
                        { type: 'line', x1: 15, y1: 0, x2: 15, y2: 10, hidden: true },
                        { type: 'line', x1: 35, y1: 0, x2: 35, y2: 10, hidden: true },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 18, x2: 50, y2: 18, from: 10, label: '50' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 10, from: 0, label: '10' },
                    ],
                },
                side: {
                    w: 30, h: 10,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 30, h: 10 },
                        { type: 'line', x1: 5, y1: 0, x2: 5, y2: 10, hidden: true },
                        { type: 'line', x1: 25, y1: 0, x2: 25, y2: 10, hidden: true },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 18, x2: 30, y2: 18, from: 10, label: '30' },
                    ],
                },
                top: {
                    w: 50, h: 30,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 50, h: 30 },
                        { type: 'circle', cx: 25, cy: 15, r: 10 },
                    ],
                    dims: [
                        { orient: 'h', x1: 15, y1: 38, x2: 35, y2: 38, from: 30, label: 'Ø20' },
                    ],
                },
            },
            criteria: [
                { kind: 'size', value: [50, 30, 10], tolerance: 0.5, label: 'Габариты пластины' },
                { kind: 'holes', value: 1, label: 'Отверстие' },
                { kind: 'bodyCount', value: 1, label: 'Одно тело' },
            ],
            hints: [
                'Окружность внутри прямоугольника при выдавливании даст дырку — '
                + 'если выдавливать только кольцо между ними.',
                'Что произойдёт, если выдавить область между прямоугольником и '
                + 'окружностью, а саму окружность не трогать?',
                'В эскизе нарисуй прямоугольник 50 × 30 и окружность Ø20 в центре. '
                + 'При выдавливании укажи внешнюю область — окружность станет '
                + 'отверстием.',
            ],
        },

        {
            id: 'fp-03-extrude',
            title: 'Выдавливание и вырез',
            goal: 'Понять, что выдавливать можно и наружу, и внутрь.',
            theory: [
                'Закрыл эскиз — нажми синюю кнопку «Выдавить» в группе «Создать». '
                + 'Редактор попросит высоту: впиши число, и плоская фигура '
                + 'поднимется в объёмную деталь.',
                'Выдавливание добавляет материал: плоская фигура вытягивается '
                + 'и становится телом.',
                'Но у той же операции есть второй режим — вырез. Тогда фигура '
                + 'не добавляет материал, а прогрызает его насквозь или на '
                + 'заданную глубину. Так делают пазы, окна, углубления.',
                'Эскиз для выреза рисуют прямо на грани готовой детали: щёлкни '
                + 'по грани и создай эскиз на ней.',
            ],
            demos: [
                { kind: 'ui-extrude' },
                { kind: 'extrude' },
                { kind: 'ui-hole' },
                { kind: 'cut' },
            ],
            steps: [
                'Построй основание 60 × 40 × 15 мм',
                'Создай эскиз на верхней грани',
                'Нарисуй прямоугольник 30 × 20 в центре',
                'Выдави его как вырез на глубину 8 мм',
            ],
            task: 'Построй брусок 60 × 40 × 15 мм (эскиз 60 на 40, высота 15) '
                + 'и сделай в верхней грани прямоугольное углубление 30 × 20 мм '
                + 'глубиной 8 мм.',
            blueprint: {
                front: {
                    w: 60, h: 15,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 60, h: 15 },
                        // Углубление сверху: видно как вырез в контуре.
                        { type: 'line', x1: 15, y1: 0, x2: 15, y2: 8 },
                        { type: 'line', x1: 45, y1: 0, x2: 45, y2: 8 },
                        { type: 'line', x1: 15, y1: 8, x2: 45, y2: 8 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 23, x2: 60, y2: 23, from: 15, label: '60' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 15, from: 0, label: '15' },
                        { orient: 'v', x1: 68, y1: 0, x2: 68, y2: 8, from: 60, label: '8' },
                    ],
                },
                side: {
                    w: 40, h: 15,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 40, h: 15 },
                        { type: 'line', x1: 10, y1: 0, x2: 10, y2: 8 },
                        { type: 'line', x1: 30, y1: 0, x2: 30, y2: 8 },
                        { type: 'line', x1: 10, y1: 8, x2: 30, y2: 8 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 23, x2: 40, y2: 23, from: 15, label: '40' },
                    ],
                },
                top: {
                    w: 60, h: 40,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 60, h: 40 },
                        { type: 'rect', x: 15, y: 10, w: 30, h: 20 },
                    ],
                    dims: [
                        { orient: 'h', x1: 15, y1: 48, x2: 45, y2: 48, from: 40, label: '30' },
                        { orient: 'v', x1: 68, y1: 10, x2: 68, y2: 30, from: 60, label: '20' },
                    ],
                },
                note: 'Углубление 30 × 20 мм на глубину 8 мм. Все размеры в миллиметрах.',
            },
            criteria: [
                { kind: 'size', value: [60, 40, 15], tolerance: 0.5, label: 'Габариты' },
                { kind: 'volume', value: 60 * 40 * 15 - 30 * 20 * 8, tolerance: 0.05,
                    label: 'Объём с углублением',
                    hint: 'Углубление либо не сделано, либо получилось не той глубины. '
                        + 'Проверь: 30 × 20 мм, глубина 8 мм.' },
                { kind: 'bodyCount', value: 1, label: 'Одно тело' },
            ],
            hints: [
                'Углубление — это тоже выдавливание, только в другую сторону и '
                + 'с другим действием.',
                'В свойствах выдавливания есть выбор операции. Что выбрать, '
                + 'чтобы материал убирался, а не добавлялся?',
                'Эскиз на верхней грани → «Выдавить» → операция «Вырез», '
                + 'расстояние 8 мм.',
            ],
        },

        {
            id: 'fp-04-keychain',
            title: 'Брелок с именем',
            goal: 'Сделать вещь, которую можно распечатать и носить с собой.',
            theory: [
                'Первая настоящая деталь. В ней сразу три приёма: пластина, '
                + 'текст на ней и отверстие под кольцо.',
                'Текст в редакторе — не картинка, а такой же эскиз, как '
                + 'прямоугольник. Его можно выдавить наружу (буквы выпуклые) '
                + 'или вырезать внутрь (буквы утоплены).',
                'Отверстие под кольцо делают близко к краю, но не вплотную: '
                + 'если стенка тоньше двух миллиметров, брелок оторвётся по ней.',
                'Порядок такой: сначала пластина, потом скругления по углам, '
                + 'потом отверстие под кольцо, и в самом конце — текст. Так '
                + 'проще всего: буквы не мешают выбирать рёбра и грани.',
            ],
            demos: [
                { kind: 'ui-fillet' },
                { kind: 'fillet' },
                { kind: 'ui-hole' },
                { kind: 'hole' },
            ],
            steps: [
                'Построй пластину 50 × 20 × 4 мм',
                'Скругли углы радиусом 4 мм',
                'Сделай отверстие Ø4 мм у левого края',
                'Напиши своё имя текстом на верхней грани',
                'Выдави буквы на 1 мм наружу',
            ],
            task: 'Сделай брелок: пластина 50 × 20 × 4 мм, скруглённые углы, '
                + 'отверстие Ø4 мм под кольцо, своё имя выпуклыми буквами.',
            /*
             * Критерии закрывают ВСЕ пять шагов задания.
             *
             * 🔴 Раньше их было два (габариты + скругление), а отверстие,
             * текст и выдавливание букв не проверялись вовсе: урок
             * засчитывался за половину работы. Проверка должна спрашивать
             * ровно то, что просит задание, иначе она учит не доделывать.
             *
             * Допуск по габаритам ужат с 1.2 до 0.6 мм: при 1.2 проходила
             * пластина толщиной 4 мм, то есть БЕЗ выдавленных букв —
             * а именно этот миллиметр и отличает готовый брелок.
             */
            criteria: [
                { kind: 'size', value: [50, 20, 5], tolerance: 0.6,
                    label: 'Габариты брелока',
                    hint: 'Пластина 50 × 20 мм, толщина 4 мм, плюс миллиметр '
                        + 'выпуклых букв сверху — итого 5 мм по высоте.' },
                { kind: 'feature', value: ['fillet'], label: 'Скругление углов',
                    hint: 'Углы острые — брелок будет колоться в кармане. '
                        + 'Скругли их радиусом 4 мм.' },
                /* Отверстие проверяем ПО ОПЕРАЦИИ, а не счётчиком holes:
                   у скруглённых углов боковины — тоже цилиндры, и счётчик
                   даёт 4 «отверстия» на пустой пластине (замерено). */
                { kind: 'feature', value: ['hole'], label: 'Отверстие под кольцо',
                    hint: 'Нужно отверстие Ø4 мм у левого края — '
                        + 'без него брелок не повесить на кольцо.' },
                { kind: 'feature', value: ['text'], label: 'Имя на брелоке',
                    hint: 'На верхней грани должно быть твоё имя: '
                        + 'инструмент «Текст» в эскизе.' },
            ],
            hints: [
                'Скругление ставят на готовой детали: выбираешь ребро и '
                + 'задаёшь радиус.',
                'Буквы должны быть выпуклыми — значит, выдавливать их надо от '
                + 'поверхности наружу. Какая операция это делает?',
                'Текст → эскиз на верхней грани → «Выдавить» 1 мм, операция '
                + '«Объединить». Углы: выбери четыре вертикальных ребра, '
                + '«Скругление» 4 мм.',
            ],
        },

        {
            id: 'fp-05-fillet',
            title: 'Скругление и фаска',
            goal: 'Понимать, где нужно скругление, а где фаска.',
            theory: [
                'Острый угол — слабое место детали: нагрузка собирается в одной '
                + 'точке, и деталь ломается именно там. Скругление размазывает '
                + 'нагрузку по дуге, и деталь держит заметно больше.',
                'Фаска — это срезанный под углом край. Её ставят не для '
                + 'прочности, а чтобы деталь легче входила в отверстие и чтобы '
                + 'край не резал руки.',
                'Простое правило: внутренний угол, где что-то может треснуть — '
                + 'скругление. Наружный край, за который берутся руками или '
                + 'который куда-то вставляют — фаска.',
            ],
            demos: [
                { kind: 'ui-fillet' },
                { kind: 'fillet' },
                { kind: 'chamfer' },
            ],
            steps: [
                'Построй брусок 40 × 40 × 20 мм',
                'Скругли четыре вертикальных ребра радиусом 6 мм',
                'Поставь фаску 2 мм на верхнем контуре',
            ],
            task: 'Брусок 40 × 40 × 20 мм (эскиз 40 на 40, высота 20): '
                + 'вертикальные рёбра скруглены R6, верхний контур с фаской 2 мм.',
            blueprint: {
                front: {
                    w: 40,
                    h: 20,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 40, h: 20 },
                        { type: 'line', x1: 0, y1: 18, x2: 2, y2: 20 },
                        { type: 'line', x1: 40, y1: 18, x2: 38, y2: 20 },
                        { type: 'line', x1: 0, y1: 18, x2: 40, y2: 18 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 28, x2: 40, y2: 28, from: 20, label: '40' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 20, from: 0, label: '20' },
                        { orient: 'v', x1: 48, y1: 18, x2: 48, y2: 20, from: 40, label: '2' },
                    ]
                },
                side: {
                    w: 40,
                    h: 20,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 40, h: 20 },
                        { type: 'line', x1: 0, y1: 18, x2: 2, y2: 20 },
                        { type: 'line', x1: 40, y1: 18, x2: 38, y2: 20 },
                        { type: 'line', x1: 0, y1: 18, x2: 40, y2: 18 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 28, x2: 40, y2: 28, from: 20, label: '40' },
                    ]
                },
                top: {
                    w: 40,
                    h: 40,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 40, h: 40 },
                        { type: 'circle', cx: 6, cy: 6, r: 6 },
                        { type: 'circle', cx: 34, cy: 6, r: 6 },
                        { type: 'circle', cx: 6, cy: 34, r: 6 },
                        { type: 'circle', cx: 34, cy: 34, r: 6 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 48, x2: 40, y2: 48, from: 40, label: '40' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 40, from: 0, label: '40' },
                        { orient: 'h', x1: 0, y1: 56, x2: 6, y2: 56, from: 48, label: 'R6' },
                    ]
                },
                note: 'Скругления R6 стоят на четырёх ВЕРТИКАЛЬНЫХ рёбрах — на '
                    + 'виде сверху они видны как закруглённые углы. Фаска 2 мм '
                    + 'идёт по верхнему контуру: на видах спереди и слева это '
                    + 'узкая полоска у верхней кромки.'
            },
            criteria: [
                { kind: 'size', value: [40, 40, 20], tolerance: 0.5, label: 'Габариты' },
                { kind: 'feature', value: ['fillet', 'chamfer'],
                    label: 'Скругление и фаска' },
            ],
            hints: [
                'Скругление и фаска — разные инструменты, оба работают по рёбрам.',
                'Сначала скругления, потом фаска. Почему такой порядок важен?',
                'Выбери четыре вертикальных ребра → «Скругление» R6. Затем '
                + 'верхний контур → «Фаска» 2 мм.',
            ],
        },

        {
            id: 'fp-06-holes',
            title: 'Отверстия под винты',
            goal: 'Делать отверстия нужного диаметра — сквозные и глухие.',
            theory: [
                'Отверстие под винт делают чуть больше самого винта, иначе он '
                + 'не войдёт. Для винта М3 сверлят 3,2–3,4 мм, для М4 — 4,2–4,5 мм.',
                'Сквозное отверстие проходит деталь насквозь. Глухое имеет дно — '
                + 'его задают глубиной.',
                'Если отверстие ближе трёх своих диаметров к краю, стенка '
                + 'треснет при затяжке винта.',
            ],
            demos: [
                { kind: 'ui-hole' },
                { kind: 'hole', count: 4 },
            ],
            steps: [
                'Построй пластину 80 × 60 × 6 мм',
                'Сделай четыре сквозных отверстия Ø3,3 мм по углам',
                'Отступи от краёв по 10 мм',
            ],
            task: 'Пластина 80 × 60 × 6 мм с четырьмя сквозными отверстиями '
                + 'Ø3,3 мм по углам, с отступом 10 мм от каждого края.',
            blueprint: {
                front: {
                    w: 80, h: 6,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 80, h: 6 },
                        { type: 'line', x1: 8.35, y1: 0, x2: 8.35, y2: 6, hidden: true },
                        { type: 'line', x1: 11.65, y1: 0, x2: 11.65, y2: 6, hidden: true },
                        { type: 'line', x1: 68.35, y1: 0, x2: 68.35, y2: 6, hidden: true },
                        { type: 'line', x1: 71.65, y1: 0, x2: 71.65, y2: 6, hidden: true },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 14, x2: 80, y2: 14, from: 6, label: '80' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 6, from: 0, label: '6' },
                    ],
                },
                side: {
                    w: 60, h: 6,
                    shapes: [{ type: 'rect', x: 0, y: 0, w: 60, h: 6 }],
                    dims: [
                        { orient: 'h', x1: 0, y1: 14, x2: 60, y2: 14, from: 6, label: '60' },
                    ],
                },
                top: {
                    w: 80, h: 60,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 80, h: 60 },
                        { type: 'circle', cx: 10, cy: 10, r: 1.65 },
                        { type: 'circle', cx: 70, cy: 10, r: 1.65 },
                        { type: 'circle', cx: 10, cy: 50, r: 1.65 },
                        { type: 'circle', cx: 70, cy: 50, r: 1.65 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 68, x2: 10, y2: 68, from: 60, label: '10' },
                        { orient: 'h', x1: 8.35, y1: 84, x2: 11.65, y2: 84, from: 76, label: '⊘3,3' },
                        { orient: 'v', x1: 88, y1: 0, x2: 88, y2: 10, from: 80, label: '10' },
                        { orient: 'h', x1: 10, y1: 76, x2: 70, y2: 76, from: 60, label: '60' },
                    ],
                },
                note: 'Четыре отверстия Ø3,3 мм. Отступ 10 мм от каждого края — '
                    + 'до ЦЕНТРА отверстия. Все размеры в миллиметрах.',
            },
            criteria: [
                { kind: 'size', value: [80, 60, 6], tolerance: 0.5, label: 'Габариты пластины' },
                { kind: 'holes', value: 4, label: 'Четыре отверстия' },
                { kind: 'bodyCount', value: 1, label: 'Одно тело' },
            ],
            hints: [
                'Четыре одинаковых отверстия можно нарисовать в одном эскизе — '
                + 'четырьмя окружностями.',
                'Отступ 10 мм от каждого края — от чего именно считается центр '
                + 'окружности?',
                'Эскиз на верхней грани: четыре окружности Ø3,3 с центрами в '
                + '10 мм от обоих ближайших краёв. Выдави насквозь как вырез.',
            ],
        },

        {
            id: 'fp-07-shell',
            title: 'Оболочка: из бруска — коробка',
            goal: 'Получать полые детали одной операцией.',
            theory: [
                'Коробку можно сделать вырезом: построить брусок и выгрызть '
                + 'внутренность. Но стенки тогда придётся считать вручную, а при '
                + 'изменении размеров — пересчитывать заново.',
                'Оболочка делает это сама: выбираешь грань, которую нужно убрать, '
                + 'задаёшь толщину стенки — и деталь становится полой. Изменишь '
                + 'габариты — стенки останутся такими же.',
                'Толщина стенки для печати — от 1,5 мм. Тоньше получится хрупко.',
            ],
            demos: [
                { kind: 'ui-shell' },
                { kind: 'shell' },
            ],
            steps: [
                'Построй брусок 70 × 50 × 30 мм',
                'Выбери верхнюю грань',
                'Примени «Оболочку» с толщиной стенки 2 мм',
            ],
            task: 'Коробка без крышки: наружные габариты 70 × 50 × 30 мм, '
                + 'толщина стенки 2 мм, верх открыт.',
            blueprint: {
                front: {
                    w: 70,
                    h: 30,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 70, h: 30 },
                        { type: 'line', x1: 2, y1: 0, x2: 2, y2: 28 },
                        { type: 'line', x1: 68, y1: 0, x2: 68, y2: 28 },
                        { type: 'line', x1: 2, y1: 28, x2: 68, y2: 28 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 38, x2: 70, y2: 38, from: 30, label: '70' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 30, from: 0, label: '30' },
                    ]
                },
                side: {
                    w: 50,
                    h: 30,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 50, h: 30 },
                        { type: 'line', x1: 2, y1: 0, x2: 2, y2: 28 },
                        { type: 'line', x1: 48, y1: 0, x2: 48, y2: 28 },
                        { type: 'line', x1: 2, y1: 28, x2: 48, y2: 28 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 38, x2: 50, y2: 38, from: 30, label: '50' },
                        { orient: 'h', x1: 0, y1: 46, x2: 2, y2: 46, from: 50, label: '2' },
                    ]
                },
                top: {
                    w: 70,
                    h: 50,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 70, h: 50 },
                        { type: 'rect', x: 2, y: 2, w: 66, h: 46 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 58, x2: 70, y2: 58, from: 50, label: '70' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 50, from: 0, label: '50' },
                    ]
                },
                note: 'Коробка без крышки: верх открыт. На виде сверху виден '
                    + 'внутренний контур — полость 66 × 46 мм. Толщина стенки 2 '
                    + 'мм одинакова со всех сторон, включая дно.'
            },
            criteria: [
                { kind: 'size', value: [70, 50, 30], tolerance: 0.5,
                    label: 'Наружные габариты' },
                { kind: 'feature', value: ['shell'], label: 'Оболочка' },
                { kind: 'volume', value: 70 * 50 * 30 - 66 * 46 * 28, tolerance: 0.08,
                    label: 'Объём стенок',
                    hint: 'Объём не сходится — проверь толщину стенки: нужно 2 мм.' },
            ],
            hints: [
                'Оболочка работает по выбранной грани: та грань, которую '
                + 'выберешь, станет отверстием.',
                'Если не выбрать ни одной грани, что получится — полая коробка '
                + 'или закрытый пузырь?',
                'Щёлкни по верхней грани бруска, возьми «Оболочка», задай '
                + 'толщину 2 мм.',
            ],
        },

        {
            id: 'fp-08-stand',
            title: 'Проект: подставка под телефон',
            goal: 'Собрать всё изученное в одной вещи — своей собственной.',
            theory: [
                'Итоговая работа курса. Здесь нет пошаговой инструкции: есть '
                + 'требования, а как их выполнить — решаешь сам.',
                'Подставка должна держать телефон под наклоном и не '
                + 'опрокидываться. Наклон удобнее всего 60–70 градусов от стола: '
                + 'меньше — экран запрокинут, больше — телефон соскальзывает.',
                'Проверь свои размеры на настоящем телефоне: измерь толщину '
                + 'линейкой и добавь пару миллиметров запаса на чехол.',
            ],
            demos: [{ kind: 'assembly' }, { kind: 'printer' }],
            steps: [
                'Измерь свой телефон: ширину и толщину',
                'Продумай форму: основание, наклонная спинка, упор снизу',
                'Построй основание не меньше 70 × 80 мм',
                'Сделай паз под телефон шире его толщины на 2 мм',
                'Скругли острые края',
            ],
            task: 'Спроектируй подставку под свой телефон. Основание не меньше '
                + '70 × 80 мм, высота от 60 мм. Паз под телефон, наклон спинки '
                + '60–70°, скруглённые края. Размеры — по своему телефону.',
            criteria: [
                { kind: 'bodyCount', value: 1, label: 'Одна деталь' },
                { kind: 'feature', value: ['fillet'], label: 'Скруглённые края',
                    hint: 'Края острые — скругли их, подставку берут руками.' },
                /*
                 * Размеры подставки ученик выбирает по своему телефону,
                 * поэтому жёстко их не задаём. Но нижнюю границу из задания
                 * («основание не меньше 70 × 80 мм, высота от 60 мм»)
                 * проверить надо — иначе засчитывался любой скруглённый
                 * кубик. Центр допуска берём с запасом над минимумом,
                 * ширина допуска покрывает реальный разброс телефонов.
                 */
                { kind: 'size', value: [80, 95, 75], tolerance: 22,
                    label: 'Размеры подставки',
                    hint: 'Основание не меньше 70 × 80 мм, высота от 60 мм — '
                        + 'подставка должна держать телефон, а не быть макетом.' },
            ],
            freeform: true,
            hints: [
                'Начни с вида сбоку: нарисуй профиль подставки в эскизе, потом '
                + 'выдави его на ширину.',
                'Что должно быть у подставки, чтобы телефон не съезжал вниз?',
                'Эскиз сбоку: треугольник с упором внизу. Выдави на 70–80 мм. '
                + 'Затем паз под телефон вырезом и скругления по краям.',
            ],
        },
    ],
};

/*
 * Курс 2. «Читаем чертежи» — проекции.
 *
 * Самая недооценённая тема: без неё ребёнок умеет строить, но не умеет
 * работать по документации. Приём — обратная задача: не объяснять, что такое
 * проекция, а заставить переключаться «объём ↔ чертёж» в обе стороны.
 *
 * Проверка здесь особенная: задания даются ЧЕРТЕЖОМ, а не текстом с
 * размерами. Ребёнок должен сам снять числа с видов — в этом и есть навык.
 */
const COURSE_DRAWINGS = {
    id: 'drawings',
    title: 'Читаем чертежи',
    subtitle: 'Проекции и виды',
    description: 'Научимся переходить от чертежа к детали и обратно. '
        + 'Это язык, на котором конструкторы говорят друг с другом.',
    lessons: [

        {
            id: 'dr-09-views',
            title: 'Три взгляда на предмет',
            goal: 'Понимать, откуда берутся виды на чертеже.',
            theory: [
                'Деталь объёмная, а бумага плоская. Чтобы записать деталь на '
                + 'бумаге, на неё смотрят с трёх сторон: спереди, сбоку и сверху.',
                'Виды получаются разные: спереди видно длину и высоту, сбоку — '
                + 'глубину и высоту, сверху — длину и глубину.',
                'В редакторе виды можно посмотреть кубом-навигатором: щёлкни '
                + 'по его грани, и камера встанет ровно на нужный вид.',
            ],
            steps: [
                'Построй брусок 50 × 30 × 20 мм',
                'Щёлкни по грани навигатора — посмотри вид спереди',
                'Посмотри вид сверху и вид сбоку',
                'Проверь: совпадают ли размеры с тем, что ты ожидал',
            ],
            task: 'Построй брусок 50 × 30 × 20 мм и рассмотри его со всех трёх '
                + 'сторон через куб-навигатор.',
            blueprint: {
                front: {
                    w: 50,
                    h: 20,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 50, h: 20 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 28, x2: 50, y2: 28, from: 20, label: '50' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 20, from: 0, label: '20' },
                    ]
                },
                side: {
                    w: 30,
                    h: 20,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 30, h: 20 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 28, x2: 30, y2: 28, from: 20, label: '30' },
                    ]
                },
                top: {
                    w: 50,
                    h: 30,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 50, h: 30 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 38, x2: 50, y2: 38, from: 30, label: '50' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 30, from: 0, label: '30' },
                    ]
                },
                note: 'Один и тот же брусок с трёх сторон. Обрати внимание: '
                    + 'ширина 50 мм видна и спереди, и сверху — это один и тот же '
                    + 'размер детали.'
            },
            criteria: [
                { kind: 'size', value: [50, 30, 20], tolerance: 0.5, label: 'Габариты' },
                { kind: 'bodyCount', value: 1, label: 'Одно тело' },
            ],
            hints: [
                'Куб-навигатор — маленький кубик в правом верхнем углу сцены.',
                'Что покажет вид сверху у бруска 50 × 30 × 20?',
                'Прямоугольник 50 × 30 — вид сверху берёт длину и глубину, '
                + 'а высота на нём не видна.',
            ],
        },

        {
            id: 'dr-10-to-drawing',
            title: 'От детали к чертежу',
            goal: 'По готовой детали понимать, как выглядят её виды.',
            theory: [
                'Обратная задача к прошлому уроку: у тебя есть деталь, надо '
                + 'представить её чертёж. Начинай с габаритов.',
                'Три габарита дают три вида, и ни одного лишнего числа: '
                + 'спереди — длина и высота, сбоку — глубина и высота, '
                + 'сверху — длина и глубина.',
                'Если в детали есть отверстие, на одном виде оно будет кругом, '
                + 'а на других — двумя линиями.',
            ],
            steps: [
                'Построй пластину 60 × 40 × 12 мм',
                'Сделай сквозное отверстие Ø16 мм в центре',
                'Посмотри вид сверху — там отверстие видно кругом',
                'Посмотри вид спереди — отверстие спрячется',
            ],
            task: 'Пластина 60 × 40 × 12 мм со сквозным отверстием Ø16 мм '
                + 'посередине. Рассмотри все три вида.',
            blueprint: {
                front: {
                    w: 60,
                    h: 12,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 60, h: 12 },
                        { type: 'line', x1: 22, y1: 0, x2: 22, y2: 12, hidden: true },
                        { type: 'line', x1: 38, y1: 0, x2: 38, y2: 12, hidden: true },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 20, x2: 60, y2: 20, from: 12, label: '60' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 12, from: 0, label: '12' },
                    ]
                },
                side: {
                    w: 40,
                    h: 12,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 40, h: 12 },
                        { type: 'line', x1: 12, y1: 0, x2: 12, y2: 12, hidden: true },
                        { type: 'line', x1: 28, y1: 0, x2: 28, y2: 12, hidden: true },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 20, x2: 40, y2: 20, from: 12, label: '40' },
                    ]
                },
                top: {
                    w: 60,
                    h: 40,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 60, h: 40 },
                        { type: 'circle', cx: 30, cy: 20, r: 8 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 48, x2: 60, y2: 48, from: 40, label: '60' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 40, from: 0, label: '40' },
                        { orient: 'h', x1: 22, y1: 56, x2: 38, y2: 56, from: 48, label: '⊘16' },
                    ]
                },
                note: 'Отверстие круглое — но круг виден ТОЛЬКО сверху. Спереди и '
                    + 'слева оно показано штриховыми линиями: так на чертеже '
                    + 'обозначают то, что скрыто внутри детали.'
            },
            criteria: [
                { kind: 'size', value: [60, 40, 12], tolerance: 0.5, label: 'Габариты' },
                { kind: 'holes', value: 1, label: 'Отверстие' },
                { kind: 'bodyCount', value: 1, label: 'Одно тело' },
            ],
            hints: [
                'Отверстие делается окружностью в эскизе или инструментом '
                + '«Отверстие» по грани.',
                'На каком виде отверстие будет видно как круг?',
                'Отверстие идёт сверху вниз — значит, кругом оно видно '
                + 'на виде сверху.',
            ],
        },

        {
            id: 'dr-11-from-drawing',
            title: 'От чертежа к детали',
            goal: 'Построить деталь, видя только чертёж.',
            theory: [
                'Самая важная задача курса: детали нет, есть только чертёж. '
                + 'Надо снять с него размеры и построить.',
                'Порядок работы: сначала по видам определи габариты, потом '
                + 'выбери вид для эскиза — обычно тот, где деталь сложнее, — '
                + 'и выдави на размер третьей стороны.',
                'Готовую деталь обязательно сверь с чертежом: обойди её '
                + 'навигатором и проверь каждый вид.',
            ],
            steps: [
                'Прочитай чертёж: сними габариты со всех видов',
                'Нарисуй эскизом вид сверху',
                'Выдави на высоту с вида спереди',
                'Сверь готовую деталь с чертежом',
            ],
            task: 'Построй деталь по чертежу. Все размеры сними с видов — '
                + 'в тексте их нет.',
            blueprint: {
                front: {
                    w: 60, h: 15,
                    shapes: [{ type: 'rect', x: 0, y: 0, w: 60, h: 15 }],
                    dims: [
                        { orient: 'h', x1: 0, y1: 23, x2: 60, y2: 23, from: 15, label: '60' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 15, from: 0, label: '15' },
                    ],
                },
                side: {
                    w: 40, h: 15,
                    shapes: [{ type: 'rect', x: 0, y: 0, w: 40, h: 15 }],
                    dims: [
                        { orient: 'h', x1: 0, y1: 23, x2: 40, y2: 23, from: 15, label: '40' },
                    ],
                },
                top: {
                    w: 60, h: 40,
                    shapes: [{ type: 'rect', x: 0, y: 0, w: 60, h: 40 }],
                    dims: [],
                },
                note: 'Все размеры в миллиметрах. Читай виды: длина и глубина '
                    + 'с вида сверху, высота — с вида спереди.',
            },
            criteria: [
                { kind: 'size', value: [60, 40, 15], tolerance: 0.5,
                    label: 'Габариты по чертежу',
                    hint: 'Сверь размеры с видами: 60 в длину, 40 в глубину, '
                        + '15 в высоту.' },
                { kind: 'bodyCount', value: 1, label: 'Одно тело' },
            ],
            hints: [
                'Вид сверху показывает длину и глубину — это и есть эскиз.',
                'Где на чертеже спрятана высота детали?',
                'Высота — на виде спереди, это 15 мм. Эскиз 60 × 40, '
                + 'выдавить на 15.',
            ],
        },

        {
            id: 'dr-12-hidden',
            title: 'Невидимые линии',
            goal: 'Читать то, что спрятано внутри детали.',
            theory: [
                'У детали бывает то, чего снаружи не видно: отверстие, паз, '
                + 'полость. На чертеже это не прячут — рисуют штриховой линией.',
                'Правило: сплошная линия — то, что видишь. Штриховая — то, что '
                + 'внутри и закрыто материалом.',
                'Одно и то же отверстие на разных видах выглядит по-разному: '
                + 'на одном сплошным кругом, на другом — двумя штриховыми '
                + 'линиями.',
            ],
            steps: [
                'Построй брусок 70 × 40 × 20 мм',
                'Сделай глухое отверстие Ø14 мм глубиной 12 мм',
                'Посмотри сверху — отверстие видно кругом',
                'Посмотри спереди — представь, где пройдут штриховые линии',
            ],
            task: 'Брусок 70 × 40 × 20 мм с глухим отверстием Ø14 мм '
                + 'глубиной 12 мм в верхней грани.',
            blueprint: {
                front: {
                    w: 70, h: 20,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 70, h: 20 },
                        { type: 'line', x1: 28, y1: 0, x2: 28, y2: 12, hidden: true },
                        { type: 'line', x1: 42, y1: 0, x2: 42, y2: 12, hidden: true },
                        { type: 'line', x1: 28, y1: 12, x2: 42, y2: 12, hidden: true },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 28, x2: 70, y2: 28, from: 20, label: '70' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 20, from: 0, label: '20' },
                        { orient: 'v', x1: 78, y1: 0, x2: 78, y2: 12, from: 70, label: '12' },
                    ],
                },
                side: {
                    w: 40, h: 20,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 40, h: 20 },
                        { type: 'line', x1: 13, y1: 0, x2: 13, y2: 12, hidden: true },
                        { type: 'line', x1: 27, y1: 0, x2: 27, y2: 12, hidden: true },
                        { type: 'line', x1: 13, y1: 12, x2: 27, y2: 12, hidden: true },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 28, x2: 40, y2: 28, from: 20, label: '40' },
                    ],
                },
                top: {
                    w: 70, h: 40,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 70, h: 40 },
                        { type: 'circle', cx: 35, cy: 20, r: 7 },
                    ],
                    dims: [
                        { orient: 'h', x1: 28, y1: 48, x2: 42, y2: 48, from: 40, label: 'Ø14' },
                    ],
                },
                note: 'Штриховые линии на видах спереди и сбоку — это глухое '
                    + 'отверстие: оно есть, но снаружи его не видно.',
            },
            criteria: [
                { kind: 'size', value: [70, 40, 20], tolerance: 0.5, label: 'Габариты' },
                { kind: 'holes', value: 1, label: 'Отверстие' },
                { kind: 'bodyCount', value: 1, label: 'Одно тело' },
            ],
            hints: [
                'Глухое отверстие не проходит насквозь — у него есть дно.',
                'Чем задают глубину глухого отверстия?',
                'В свойствах отверстия выбери не «насквозь», а глубину 12 мм.',
            ],
        },

        {
            id: 'dr-13-dims',
            title: 'Размеры на чертеже',
            goal: 'Правильно читать и ставить размеры.',
            theory: [
                'Чертёж без размеров — просто картинка. Размеры превращают его '
                + 'в документ, по которому деталь можно изготовить.',
                'Размер ставят к той стороне, которую меряют. Каждое число '
                + 'указывают один раз: если длина есть на виде сверху, '
                + 'на виде спереди её не повторяют.',
                'Диаметр отверстия помечают значком Ø, радиус скругления — '
                + 'буквой R. По значку сразу понятно, что именно измерено.',
            ],
            steps: [
                'Построй пластину 90 × 50 × 8 мм',
                'Сделай два отверстия Ø6 мм с отступом 15 мм от коротких краёв',
                'Скругли углы радиусом 8 мм',
                'Проверь: все ли размеры ты можешь назвать',
            ],
            task: 'Пластина 90 × 50 × 8 мм: два отверстия Ø6 мм с отступом '
                + '15 мм от коротких краёв, углы скруглены R8.',
            criteria: [
                { kind: 'size', value: [90, 50, 8], tolerance: 0.5, label: 'Габариты' },
                { kind: 'feature', value: ['fillet'], label: 'Скругление углов' },
                { kind: 'bodyCount', value: 1, label: 'Одно тело' },
            ],
            hints: [
                'Скругление углов и отверстия — разные операции, порядок важен.',
                'Что сделать раньше: скругления или отверстия? Подумай, где '
                + 'потом будет проще выбирать рёбра.',
                'Сначала пластина, потом скругления по углам, в конце '
                + 'отверстия — так рёбра выбираются проще всего.',
            ],
        },

        {
            id: 'dr-14-section',
            title: 'Разрез',
            goal: 'Понимать чертежи с разрезом.',
            theory: [
                'Когда внутри детали много всего, штриховых линий становится '
                + 'столько, что чертёж не прочитать. Тогда деталь мысленно '
                + 'разрезают.',
                'Разрез — это вид детали, у которой убрали переднюю половину. '
                + 'Скрытое становится видно сплошными линиями, а место, где '
                + 'прошёл нож, штрихуют косыми линиями.',
                'Не путай: штриховая линия — деталь целая, внутренность '
                + 'угадывается. Штриховка косыми линиями — деталь разрезана.',
            ],
            steps: [
                'Построй коробку 60 × 60 × 40 мм',
                'Сделай оболочку с толщиной стенки 3 мм',
                'Посмотри внутрь — это и есть то, что показал бы разрез',
                'Представь, как выглядит разрез этой коробки на чертеже',
            ],
            task: 'Коробка без крышки: наружные габариты 60 × 60 × 40 мм, '
                + 'толщина стенки 3 мм.',
            blueprint: {
                front: {
                    w: 60,
                    h: 40,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 60, h: 40 },
                        { type: 'line', x1: 3, y1: 0, x2: 3, y2: 37 },
                        { type: 'line', x1: 57, y1: 0, x2: 57, y2: 37 },
                        { type: 'line', x1: 3, y1: 37, x2: 57, y2: 37 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 48, x2: 60, y2: 48, from: 40, label: '60' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 40, from: 0, label: '40' },
                    ]
                },
                side: {
                    w: 60,
                    h: 40,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 60, h: 40 },
                        { type: 'line', x1: 3, y1: 0, x2: 3, y2: 37 },
                        { type: 'line', x1: 57, y1: 0, x2: 57, y2: 37 },
                        { type: 'line', x1: 3, y1: 37, x2: 57, y2: 37 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 48, x2: 60, y2: 48, from: 40, label: '60' },
                        { orient: 'h', x1: 0, y1: 56, x2: 3, y2: 56, from: 60, label: '3' },
                    ]
                },
                top: {
                    w: 60,
                    h: 60,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 60, h: 60 },
                        { type: 'rect', x: 3, y: 3, w: 54, h: 54 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 68, x2: 60, y2: 68, from: 60, label: '60' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 60, from: 0, label: '60' },
                    ]
                },
                note: 'На видах спереди и слева показан РАЗРЕЗ: деталь мысленно '
                    + 'разрезали пополам, чтобы стало видно стенки и дно. Так на '
                    + 'чертеже показывают то, что внутри.'
            },
            criteria: [
                { kind: 'size', value: [60, 60, 40], tolerance: 0.5,
                    label: 'Наружные габариты' },
                { kind: 'feature', value: ['shell'], label: 'Оболочка' },
                { kind: 'bodyCount', value: 1, label: 'Одно тело' },
            ],
            hints: [
                'Оболочка убирает выбранную грань и оставляет стенки '
                + 'одинаковой толщины.',
                'Какую грань надо выбрать, чтобы коробка получилась '
                + 'без крышки?',
                'Щёлкни по верхней грани, возьми «Оболочка», толщина 3 мм.',
            ],
        },

        {
            id: 'dr-15-project',
            title: 'Проект: деталь по чертежу',
            goal: 'Построить деталь, имея только чертёж и ничего больше.',
            theory: [
                'Итоговая работа курса. Даётся только чертёж: три вида '
                + 'с размерами, без картинки детали и без подсказок.',
                'Порядок тот же: сними габариты, выбери вид для эскиза, '
                + 'выдави на третий размер, добавь отверстия и скругления.',
                'Проверь себя сам: обойди готовую деталь навигатором и сверь '
                + 'каждый вид с чертежом.',
            ],
            steps: [
                'Прочитай чертёж целиком, прежде чем строить',
                'Построй основание по виду сверху',
                'Выдави на высоту с вида спереди',
                'Добавь отверстия, показанные штриховыми линиями',
                'Сверь все три вида с чертежом',
            ],
            task: 'Построй деталь по чертежу: размеры и отверстия сними '
                + 'с видов самостоятельно.',
            blueprint: {
                front: {
                    w: 80, h: 18,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 80, h: 18 },
                        { type: 'line', x1: 16, y1: 0, x2: 16, y2: 18, hidden: true },
                        { type: 'line', x1: 24, y1: 0, x2: 24, y2: 18, hidden: true },
                        { type: 'line', x1: 56, y1: 0, x2: 56, y2: 18, hidden: true },
                        { type: 'line', x1: 64, y1: 0, x2: 64, y2: 18, hidden: true },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 26, x2: 80, y2: 26, from: 18, label: '80' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 18, from: 0, label: '18' },
                    ],
                },
                side: {
                    w: 50, h: 18,
                    shapes: [{ type: 'rect', x: 0, y: 0, w: 50, h: 18 }],
                    dims: [
                        { orient: 'h', x1: 0, y1: 26, x2: 50, y2: 26, from: 18, label: '50' },
                    ],
                },
                top: {
                    w: 80, h: 50,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 80, h: 50 },
                        { type: 'circle', cx: 20, cy: 25, r: 4 },
                        { type: 'circle', cx: 60, cy: 25, r: 4 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 58, x2: 20, y2: 58, from: 50, label: '20' },
                        { orient: 'h', x1: 20, y1: 66, x2: 60, y2: 66, from: 50, label: '40' },
                    ],
                },
                note: 'Два сквозных отверстия Ø8 мм. Штриховые линии на виде '
                    + 'спереди показывают, что отверстия проходят насквозь.',
            },
            criteria: [
                { kind: 'size', value: [80, 50, 18], tolerance: 0.5,
                    label: 'Габариты по чертежу' },
                { kind: 'holes', value: 2, label: 'Два отверстия' },
                { kind: 'bodyCount', value: 1, label: 'Одно тело' },
            ],
            hints: [
                'Начни с вида сверху — он даёт длину, глубину и положение '
                + 'отверстий.',
                'Сколько отверстий на чертеже и где их центры?',
                'Эскиз 80 × 50, выдавить на 18. Отверстия Ø8 с центрами '
                + 'в 20 и 60 мм от левого края, посередине по глубине.',
            ],
        },
    ],
};


/*
 * Курс 3. «Детали, которые собираются» — посадки и зазоры.
 *
 * Самая практичная тема курса: здесь ребёнок узнаёт, почему его первая
 * напечатанная коробка не закрылась. Главное понятие — зазор, и оно
 * контринтуитивное: чтобы детали собрались, они НЕ должны совпадать
 * по размеру.
 *
 * ⚠️ Проверка зазора. Критерий `gap` считает просвет по разнице наименьших
 * сечений двух тел, поэтому задания на сборку должны давать РОВНО ДВА тела:
 * коробку и крышку. Если ребёнок построит лишнее, критерий это заметит.
 */
const COURSE_ASSEMBLY = {
    id: 'assembly',
    title: 'Детали, которые собираются',
    subtitle: 'Посадки и зазоры',
    description: 'Научимся делать вещи, которые реально надеваются, '
        + 'вставляются и крутятся. Здесь начинается настоящее '
        + 'проектирование.',
    lessons: [

        {
            id: 'as-16-gap',
            title: 'Зазор',
            goal: 'Понять, почему деталь «точно по размеру» не влезает.',
            theory: [
                'Сделай вал 20 мм и отверстие 20 мм — и они не соберутся. '
                + 'У всего есть погрешность: принтер кладёт пластик чуть '
                + 'толще, деталь при остывании чуть ведёт.',
                'Поэтому отверстие всегда делают больше вала. Разница между '
                + 'ними и называется зазором.',
                'Для печатных деталей обычно берут зазор от 0,2 до 0,4 мм. '
                + 'Этого хватает, чтобы собралось, и деталь не болтается.',
            ],
            steps: [
                'Построй пластину 60 × 40 × 10 мм',
                'Сделай в ней отверстие Ø20,4 мм',
                'Посчитай: какой зазор будет для вала Ø20?',
                'Проверь работу',
            ],
            task: 'Пластина 60 × 40 × 10 мм с отверстием Ø20,4 мм под вал '
                + 'диаметром 20 мм.',
            blueprint: {
                front: {
                    w: 60,
                    h: 10,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 60, h: 10 },
                        { type: 'line', x1: 19.8, y1: 0, x2: 19.8, y2: 10, hidden: true },
                        { type: 'line', x1: 40.2, y1: 0, x2: 40.2, y2: 10, hidden: true },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 18, x2: 60, y2: 18, from: 10, label: '60' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 10, from: 0, label: '10' },
                    ]
                },
                side: {
                    w: 40,
                    h: 10,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 40, h: 10 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 18, x2: 40, y2: 18, from: 10, label: '40' },
                    ]
                },
                top: {
                    w: 60,
                    h: 40,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 60, h: 40 },
                        { type: 'circle', cx: 30, cy: 20, r: 10.2 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 48, x2: 60, y2: 48, from: 40, label: '60' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 40, from: 0, label: '40' },
                        { orient: 'h', x1: 19.8, y1: 56, x2: 40.2, y2: 56, from: 48, label: '⊘20,4' },
                    ]
                },
                note: 'Отверстие ⊘20,4 мм под вал ⊘20 мм. Разница 0,4 мм на '
                    + 'диаметр — это 0,2 мм на сторону: вал войдёт свободно, но '
                    + 'без люфта.'
            },
            criteria: [
                { kind: 'size', value: [60, 40, 10], tolerance: 0.5, label: 'Габариты' },
                { kind: 'holes', value: 1, label: 'Отверстие' },
                { kind: 'bodyCount', value: 1, label: 'Одно тело' },
            ],
            hints: [
                'Зазор на сторону — половина разницы диаметров.',
                'Вал 20, отверстие 20,4. Сколько остаётся с каждой стороны?',
                'По 0,2 мм с каждой стороны — это и есть свободная посадка.',
            ],
        },

        {
            id: 'as-17-fits',
            title: 'Посадки',
            goal: 'Выбирать зазор под задачу: свободно, плотно или намертво.',
            theory: [
                'Свободная посадка — зазор побольше, 0,3–0,5 мм. Деталь легко '
                + 'надевается и снимается: крышки, колёса на оси.',
                'Плотная посадка — зазор маленький, около 0,1 мм. Деталь '
                + 'надевается с усилием и не болтается: ручки, втулки.',
                'Посадка с натягом — зазора нет, деталь чуть больше отверстия. '
                + 'Её забивают, и обратно она уже не снимается.',
            ],
            steps: [
                'Построй пластину 70 × 50 × 12 мм',
                'Сделай три отверстия: Ø10,4, Ø10,1 и Ø10,0 мм',
                'Подумай, какое из них для какой посадки',
                'Проверь работу',
            ],
            task: 'Пластина 70 × 50 × 12 мм с тремя отверстиями: Ø10,4 мм '
                + '(свободная), Ø10,1 мм (плотная) и Ø10 мм (с натягом).',
            blueprint: {
                front: {
                    w: 70,
                    h: 12,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 70, h: 12 },
                        { type: 'line', x1: 12.3, y1: 0, x2: 12.3, y2: 12, hidden: true },
                        { type: 'line', x1: 22.7, y1: 0, x2: 22.7, y2: 12, hidden: true },
                        { type: 'line', x1: 29.95, y1: 0, x2: 29.95, y2: 12, hidden: true },
                        { type: 'line', x1: 40.05, y1: 0, x2: 40.05, y2: 12, hidden: true },
                        { type: 'line', x1: 47.5, y1: 0, x2: 47.5, y2: 12, hidden: true },
                        { type: 'line', x1: 57.5, y1: 0, x2: 57.5, y2: 12, hidden: true },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 20, x2: 70, y2: 20, from: 12, label: '70' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 12, from: 0, label: '12' },
                    ]
                },
                side: {
                    w: 50,
                    h: 12,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 50, h: 12 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 20, x2: 50, y2: 20, from: 12, label: '50' },
                    ]
                },
                top: {
                    w: 70,
                    h: 50,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 70, h: 50 },
                        { type: 'circle', cx: 17.5, cy: 25, r: 5.2 },
                        { type: 'circle', cx: 35, cy: 25, r: 5.05 },
                        { type: 'circle', cx: 52.5, cy: 25, r: 5 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 58, x2: 70, y2: 58, from: 50, label: '70' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 50, from: 0, label: '50' },
                        { orient: 'h', x1: 12.3, y1: 82, x2: 22.7, y2: 82, from: 50, label: '⊘10,4' },
                        { orient: 'h', x1: 29.95, y1: 74, x2: 40.05, y2: 74, from: 50, label: '⊘10,1' },
                        { orient: 'h', x1: 47.5, y1: 66, x2: 57.5, y2: 66, from: 50, label: '⊘10' },
                    ]
                },
                note: 'Три отверстия под один и тот же вал ⊘10 мм. Слева '
                    + 'свободная посадка (⊘10,4), в середине плотная (⊘10,1), '
                    + 'справа с натягом (⊘10) — её запрессовывают.'
            },
            criteria: [
                { kind: 'size', value: [70, 50, 12], tolerance: 0.5, label: 'Габариты' },
                { kind: 'holes', value: 3, label: 'Три отверстия' },
                { kind: 'bodyCount', value: 1, label: 'Одно тело' },
            ],
            hints: [
                'Три отверстия удобно нарисовать в одном эскизе.',
                'Чем отличаются числа 10,4 и 10,0 для вала Ø10?',
                'Эскиз с тремя окружностями разного диаметра, потом вырез '
                + 'насквозь.',
            ],
        },

        {
            id: 'as-18-lid',
            title: 'Коробка с крышкой',
            goal: 'Сделать две детали, которые собираются друг с другом.',
            theory: [
                'Первая настоящая сборка. Крышка должна сниматься, но не '
                + 'болтаться — значит, нужна свободная посадка с зазором '
                + 'около 0,3 мм.',
                'Сначала строим коробку: брусок и оболочка. Потом крышку — '
                + 'её внутренний размер делаем на 0,3 мм больше наружного '
                + 'размера коробки.',
                'Если крышка не наделась — увеличь зазор. Болтается — '
                + 'уменьши. Точное число подбирают на практике: оно зависит '
                + 'от принтера.',
            ],
            steps: [
                'Построй коробку 60 × 40 × 30 мм, стенка 2 мм',
                'Построй крышку: эскиз 60,3 × 40,3 мм',
                'Выдави крышку на 12 мм и сделай ей оболочку 2 мм',
                'Проверь: получилось два тела',
            ],
            task: 'Коробка 60 × 40 × 30 мм со стенкой 2 мм и крышка-колпачок '
                + 'с внутренним размером на 0,3 мм больше. Должно получиться '
                + 'два отдельных тела.',
            /*
             * Чертёж двух деталей сразу: коробка и крышка рядом, как их и
             * будут собирать. Главный вид показывает высоты, вид сверху —
             * посадочные размеры, по которым крышка садится на коробку.
             */
            blueprint: {
                front: {
                    w: 154.3, h: 30,
                    shapes: [
                        { type: 'label', x: 30, y: -8, text: 'Деталь 1 — коробка' },
                        // Коробка: наружный контур и стенки изнутри.
                        { type: 'rect', x: 0, y: 0, w: 60, h: 30 },
                        { type: 'line', x1: 2, y1: 0, x2: 2, y2: 28 },
                        { type: 'line', x1: 58, y1: 0, x2: 58, y2: 28 },
                        { type: 'line', x1: 2, y1: 28, x2: 58, y2: 28 },
                        // Толщина стенки — выноской: цепочка размеров тут не помещается.
                        { type: 'lead', x: 1, y: 14, tx: -14, ty: 6, text: 'стенка 2' },
                        // Разделитель: детали отдельные, а не одно тело с уступом.
                        { type: 'line', x1: 76, y1: -14, x2: 76, y2: 44, hidden: true },
                        { type: 'label', x: 122, y: -8, text: 'Деталь 2 — крышка' },
                        /*
                         * Крышка-колпачок, открытый вниз. Наружный габарит
                         * 64,3 — внутренний 60,3 плюс две стенки по 2 мм.
                         * Раньше здесь стояло 60,3, и вид спереди расходился
                         * с видом сверху: один и тот же габарит был разным.
                         */
                        { type: 'rect', x: 90, y: 0, w: 64.3, h: 12 },
                        { type: 'line', x1: 92, y1: 0, x2: 92, y2: 10 },
                        { type: 'line', x1: 152.3, y1: 0, x2: 152.3, y2: 10 },
                        { type: 'line', x1: 92, y1: 10, x2: 152.3, y2: 10 },
                    ],
                    dims: [
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 30, from: 0, label: '30' },
                        { orient: 'h', x1: 0, y1: 38, x2: 60, y2: 38, from: 30, label: '60' },
                        { orient: 'v', x1: 162, y1: 0, x2: 162, y2: 12, from: 154.3, label: '12' },
                        { orient: 'h', x1: 90, y1: 38, x2: 154.3, y2: 38, from: 12, label: '64,3' },
                        // Внутренний размер — им крышка и садится на коробку.
                        { orient: 'h', x1: 92, y1: 48, x2: 152.3, y2: 48, from: 38, label: '60,3' },
                    ],
                },
                side: {
                    w: 40, h: 30,
                    shapes: [
                        { type: 'label', x: 20, y: -8, text: 'Коробка сбоку' },
                        { type: 'rect', x: 0, y: 0, w: 40, h: 30 },
                        { type: 'line', x1: 2, y1: 0, x2: 2, y2: 28 },
                        { type: 'line', x1: 38, y1: 0, x2: 38, y2: 28 },
                        { type: 'line', x1: 2, y1: 28, x2: 38, y2: 28 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 38, x2: 40, y2: 38, from: 30, label: '40' },
                        { orient: 'v', x1: -10, y1: 0, x2: -10, y2: 30, from: 0, label: '30' },
                    ],
                },
                top: {
                    w: 154.3, h: 44,
                    shapes: [
                        { type: 'label', x: 30, y: -8, text: 'Деталь 1 — коробка' },
                        // Коробка сверху: наружный контур и внутренняя полость.
                        { type: 'rect', x: 0, y: 2, w: 60, h: 40 },
                        { type: 'rect', x: 2, y: 4, w: 56, h: 36 },
                        { type: 'line', x1: 75, y1: -12, x2: 75, y2: 56, hidden: true },
                        { type: 'label', x: 122, y: -8, text: 'Деталь 2 — крышка' },
                        // Крышка сверху: снаружи 64,3, внутри 60,3.
                        { type: 'rect', x: 90, y: 0, w: 64.3, h: 44 },
                        { type: 'rect', x: 92, y: 2, w: 60.3, h: 40.3 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 50, x2: 60, y2: 50, from: 42, label: '60' },
                        { orient: 'v', x1: -8, y1: 2, x2: -8, y2: 42, from: 0, label: '40' },
                        // Наружный габарит крышки — от наружного контура.
                        { orient: 'h', x1: 90, y1: 56, x2: 154.3, y2: 56, from: 44, label: '64,3' },
                        { orient: 'v', x1: 176, y1: 0, x2: 176, y2: 44, from: 154.3, label: '44,3' },
                        // Внутренний — тот, которым крышка садится на коробку.
                        { orient: 'h', x1: 92, y1: 66, x2: 152.3, y2: 66, from: 56, label: '60,3' },
                        { orient: 'v', x1: 164, y1: 2, x2: 164, y2: 42.3, from: 152.3, label: '40,3' },
                    ],
                },
                note: 'ДВЕ ОТДЕЛЬНЫЕ ДЕТАЛИ, разделены штриховой линией: слева '
                    + 'коробка, справа крышка. У крышки проставлены ДВА размера: '
                    + '64,3 — наружный габарит (столько места она займёт), '
                    + '60,3 — внутренний, ИМ она садится на коробку. Он на '
                    + '0,3 мм больше коробки: это и есть зазор на посадку, '
                    + '0,15 мм на сторону. Высота крышки 12 мм с высотой '
                    + 'коробки не сравнивается — крышка просто накрывает её '
                    + 'сверху.',
            },
            criteria: [
                { kind: 'bodyCount', value: 2, label: 'Два тела: коробка и крышка' },
                { kind: 'feature', value: ['shell'], label: 'Оболочка' },
                { kind: 'gap', min: 0.1, max: 0.6, label: 'Зазор между деталями',
                    hint: 'Проверь размеры крышки: она должна быть больше '
                        + 'коробки на 0,2–0,4 мм.' },
            ],
            hints: [
                'Крышка — отдельное тело. При выдавливании выбери операцию '
                + '«Новое тело», а не «Объединить».',
                'На сколько крышка должна быть больше коробки?',
                'Коробка 60 × 40 снаружи, крышка изнутри 60,3 × 40,3 — '
                + 'зазор 0,15 мм на сторону.',
            ],
        },

        {
            id: 'as-19-screws',
            title: 'Отверстия под винты',
            goal: 'Знать диаметры под М3 и М4, понимать потай.',
            theory: [
                'Винт М3 — диаметром 3 мм. Отверстие под него сверлят 3,3 мм: '
                + 'винт должен проходить свободно, а не вкручиваться в пластик.',
                'Для М4 сверлят 4,3 мм. Правило простое: диаметр винта плюс '
                + '0,3 мм. Эти два размера нужны постоянно — запомни их.',
                'Потай — когда шляпка винта должна утонуть заподлицо. Тогда '
                + 'сверху отверстие расширяют конусом, и винт садится вровень '
                + 'с поверхностью.',
            ],
            steps: [
                'Построй пластину 80 × 40 × 6 мм',
                'Сделай два отверстия Ø3,3 мм под М3',
                'Сделай два отверстия Ø4,3 мм под М4',
                'Проверь работу',
            ],
            task: 'Пластина 80 × 40 × 6 мм: два отверстия Ø3,3 мм и два '
                + 'отверстия Ø4,3 мм.',
            blueprint: {
                front: {
                    w: 80,
                    h: 6,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 80, h: 6 },
                        { type: 'line', x1: 13.35, y1: 0, x2: 13.35, y2: 6, hidden: true },
                        { type: 'line', x1: 16.65, y1: 0, x2: 16.65, y2: 6, hidden: true },
                        { type: 'line', x1: 28.35, y1: 0, x2: 28.35, y2: 6, hidden: true },
                        { type: 'line', x1: 31.65, y1: 0, x2: 31.65, y2: 6, hidden: true },
                        { type: 'line', x1: 52.85, y1: 0, x2: 52.85, y2: 6, hidden: true },
                        { type: 'line', x1: 57.15, y1: 0, x2: 57.15, y2: 6, hidden: true },
                        { type: 'line', x1: 67.85, y1: 0, x2: 67.85, y2: 6, hidden: true },
                        { type: 'line', x1: 72.15, y1: 0, x2: 72.15, y2: 6, hidden: true },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 14, x2: 80, y2: 14, from: 6, label: '80' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 6, from: 0, label: '6' },
                    ]
                },
                side: {
                    w: 40,
                    h: 6,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 40, h: 6 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 14, x2: 40, y2: 14, from: 6, label: '40' },
                    ]
                },
                top: {
                    w: 80,
                    h: 40,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 80, h: 40 },
                        { type: 'circle', cx: 15, cy: 20, r: 1.65 },
                        { type: 'circle', cx: 30, cy: 20, r: 1.65 },
                        { type: 'circle', cx: 55, cy: 20, r: 2.15 },
                        { type: 'circle', cx: 70, cy: 20, r: 2.15 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 48, x2: 80, y2: 48, from: 40, label: '80' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 40, from: 0, label: '40' },
                        { orient: 'h', x1: 13.35, y1: 64, x2: 16.65, y2: 64, from: 48, label: '⊘3,3' },
                        { orient: 'h', x1: 52.85, y1: 56, x2: 57.15, y2: 56, from: 48, label: '⊘4,3' },
                    ]
                },
                note: 'Слева два отверстия ⊘3,3 мм под винт М3, справа два ⊘4,3 '
                    + 'мм под М4. Отверстие делают на 0,3 мм больше резьбы: '
                    + 'печать даёт усадку, и в размер винт не пройдёт.'
            },
            criteria: [
                { kind: 'size', value: [80, 40, 6], tolerance: 0.5, label: 'Габариты' },
                { kind: 'holes', value: 4, label: 'Четыре отверстия' },
                { kind: 'bodyCount', value: 1, label: 'Одно тело' },
            ],
            hints: [
                'Отверстия разного диаметра рисуются в одном эскизе.',
                'Какой диаметр нужен под винт М4?',
                'М3 — 3,3 мм, М4 — 4,3 мм. Всегда диаметр винта плюс 0,3.',
            ],
        },

        {
            id: 'as-20-slots',
            title: 'Пазы',
            goal: 'Делать соединения с возможностью регулировки.',
            theory: [
                'Паз — это вытянутое отверстие. Он нужен, когда деталь должна '
                + 'не просто держаться, а ещё и сдвигаться: подстроить '
                + 'положение, выбрать натяжение ремня.',
                'Ширину паза берут как у обычного отверстия под винт, а длину '
                + '— насколько нужен ход детали.',
                'Слишком длинный паз ослабляет деталь: вокруг него должно '
                + 'остаться достаточно материала.',
            ],
            steps: [
                'Построй пластину 90 × 40 × 8 мм',
                'Нарисуй паз шириной 3,3 мм и длиной 25 мм',
                'Вырежи его насквозь',
                'Проверь работу',
            ],
            task: 'Пластина 90 × 40 × 8 мм со сквозным пазом шириной 3,3 мм '
                + 'и длиной 25 мм.',
            blueprint: {
                front: {
                    w: 90,
                    h: 8,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 90, h: 8 },
                        { type: 'line', x1: 32.5, y1: 0, x2: 32.5, y2: 8, hidden: true },
                        { type: 'line', x1: 57.5, y1: 0, x2: 57.5, y2: 8, hidden: true },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 16, x2: 90, y2: 16, from: 8, label: '90' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 8, from: 0, label: '8' },
                    ]
                },
                side: {
                    w: 40,
                    h: 8,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 40, h: 8 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 16, x2: 40, y2: 16, from: 8, label: '40' },
                    ]
                },
                top: {
                    w: 90,
                    h: 40,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 90, h: 40 },
                        { type: 'rect', x: 32.5, y: 18.35, w: 25, h: 3.3 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 48, x2: 90, y2: 48, from: 40, label: '90' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 40, from: 0, label: '40' },
                        { orient: 'h', x1: 32.5, y1: 56, x2: 57.5, y2: 56, from: 48, label: '25' },
                        { orient: 'v', x1: 98, y1: 18.35, x2: 98, y2: 21.65, from: 90, label: '3,3' },
                    ]
                },
                note: 'Сквозной паз 25 × 3,3 мм. Ширина 3,3 мм — под винт М3, '
                    + 'чтобы он ходил вдоль паза; длина 25 мм задаёт, насколько '
                    + 'далеко его можно сдвинуть.'
            },
            criteria: [
                { kind: 'size', value: [90, 40, 8], tolerance: 0.5, label: 'Габариты' },
                { kind: 'bodyCount', value: 1, label: 'Одно тело' },
                /*
                 * Площадь паза: прямоугольник между центрами дуг плюс полный
                 * круг из двух полукругов на концах. Считаем точно, а не
                 * «длина × ширина»: иначе критерий врёт на 12%.
                 *
                 * ⚠️ Допуск 1%, а не обычные 5–6%: паз вынимает всего 2,2%
                 * объёма пластины. При допуске 6% критерий засчитал бы
                 * пластину вообще БЕЗ паза — то есть не проверял бы ничего.
                 */
                { kind: 'volume',
                    value: 90 * 40 * 8
                        - ((25 - 3.3) * 3.3 + Math.PI * 1.65 * 1.65) * 8,
                    tolerance: 0.01, label: 'Объём с пазом',
                    hint: 'Паз либо не прорезан насквозь, либо получился '
                        + 'не того размера.' },
            ],
            hints: [
                'В эскизе есть готовый инструмент «Паз» — не нужно рисовать '
                + 'его из линий и дуг.',
                'Паз — это прямоугольник с полукруглыми концами. Какой '
                + 'ширины он должен быть под винт М3?',
                'Ширина 3,3 мм — как отверстие под М3. Длина 25 мм — '
                + 'это ход регулировки.',
            ],
        },

        {
            id: 'as-21-snap',
            title: 'Защёлка',
            goal: 'Понять, как работает соединение без винтов.',
            theory: [
                'Защёлка держится потому, что пластик пружинит: язычок '
                + 'отгибается, проскакивает выступ и возвращается на место.',
                'Язычок должен быть достаточно длинным и тонким, чтобы '
                + 'согнуться и не сломаться. Короткий и толстый просто '
                + 'треснет.',
                'Крючок на конце делают со скосом с одной стороны: по скосу '
                + 'язычок легко заходит, а прямая сторона не даёт вылезти '
                + 'обратно.',
            ],
            steps: [
                'Построй пластину 60 × 40 × 4 мм',
                'Сделай две прорези шириной 1,5 мм — они выделят язычок',
                'Проверь: язычок получился не слишком толстым?',
                'Проверь работу',
            ],
            task: 'Пластина 60 × 40 × 4 мм с двумя прорезями шириной 1,5 мм, '
                + 'которые выделяют пружинящий язычок посередине.',
            blueprint: {
                front: {
                    w: 60,
                    h: 4,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 60, h: 4 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 12, x2: 60, y2: 12, from: 4, label: '60' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 4, from: 0, label: '4' },
                    ]
                },
                side: {
                    w: 40,
                    h: 4,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 40, h: 4 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 12, x2: 40, y2: 12, from: 4, label: '40' },
                    ]
                },
                top: {
                    w: 60,
                    h: 40,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 60, h: 40 },
                        { type: 'rect', x: 15, y: 12, w: 30, h: 1.5 },
                        { type: 'rect', x: 15, y: 26.5, w: 30, h: 1.5 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 48, x2: 60, y2: 48, from: 40, label: '60' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 40, from: 0, label: '40' },
                        { orient: 'h', x1: 15, y1: 56, x2: 45, y2: 56, from: 48, label: '30' },
                        { orient: 'v', x1: 68, y1: 12, x2: 68, y2: 13.5, from: 60, label: '1,5' },
                        { orient: 'v', x1: 76, y1: 13.5, x2: 76, y2: 26.5, from: 68, label: '13' },
                    ]
                },
                note: 'Две прорези 1,5 мм выделяют язычок шириной 13 мм — он и '
                    + 'пружинит. Прорези НЕ доходят до края: язычок держится '
                    + 'одним концом, как консоль.'
            },
            criteria: [
                { kind: 'size', value: [60, 40, 4], tolerance: 0.5, label: 'Габариты' },
                { kind: 'bodyCount', value: 1, label: 'Одно тело' },
                /*
                 * Прорези: без этого критерия сплошная пластина 60×40×4
                 * засчитывалась как готовая защёлка.
                 *
                 * Длину прорезей задание не фиксирует, поэтому берём центр
                 * диапазона (2 прорези 1,5 × 30 мм насквозь) с допуском 3%:
                 * проходят прорези длиной примерно от 15 до 50 мм, а сплошная
                 * пластина отклоняется на 3,9% и не проходит (посчитано).
                 */
                { kind: 'volume', value: 60 * 40 * 4 - 2 * 1.5 * 30 * 4, tolerance: 0.03,
                    label: 'Прорези под язычок',
                    hint: 'Нужны две сквозные прорези шириной 1,5 мм — '
                        + 'именно они выделяют пружинящий язычок.' },
            ],
            hints: [
                'Прорези — это два узких выреза, идущих от одного края '
                + 'вглубь детали.',
                'Что останется между двумя прорезями?',
                'Между прорезями остаётся полоска — она и есть язычок. '
                + 'Чем она длиннее, тем легче гнётся.',
            ],
        },

        {
            id: 'as-22-ribs',
            title: 'Стойки и рёбра жёсткости',
            goal: 'Делать детали, которые не гнутся и не ломаются.',
            theory: [
                'Плоская стенка гнётся. Чтобы деталь держала форму, к ней '
                + 'добавляют рёбра — тонкие перегородки, поставленные на ребро. '
                + 'Ребро работает как балка.',
                'Стойка — столбик с отверстием под винт. На стойках держат '
                + 'плату внутри корпуса: она не касается дна, и снизу остаётся '
                + 'место для проводов и пайки.',
                'Толщина ребра обычно равна толщине стенки, высота — в два-три '
                + 'раза больше.',
            ],
            steps: [
                'Построй основание 80 × 60 × 4 мм',
                'Добавь четыре стойки Ø8 мм высотой 6 мм',
                'Сделай в каждой стойке отверстие Ø3,3 мм под М3',
                'Проверь работу',
            ],
            task: 'Основание 80 × 60 × 4 мм с четырьмя стойками Ø8 мм высотой '
                + '6 мм. В каждой стойке отверстие Ø3,3 мм под винт М3.',
            criteria: [
                { kind: 'size', value: [80, 60, 10], tolerance: 2,
                    label: 'Габариты со стойками',
                    hint: 'Основание 80 × 60 × 4 мм плюс стойки высотой 6 мм — '
                        + 'общая высота 10 мм.' },
                /* Отверстия здесь НЕ проверяем числом: стойка — тоже цилиндр,
                   и точный счёт зависит от способа построения. Габариты
                   надёжнее и проверяют ровно то, чему учит урок. */
                { kind: 'bodyCount', value: 1, label: 'Одно тело' },
            ],
            hints: [
                'Стойка строится как обычный цилиндр: круглый эскиз на верхней '
                + 'грани и выдавливание вверх.',
                'Как сделать четыре одинаковых стойки, не рисуя каждую?',
                'Нарисуй четыре окружности в одном эскизе и выдави их все '
                + 'сразу с операцией «Объединить».',
            ],
        },

        {
            id: 'as-23-case',
            title: 'Корпус для электроники',
            goal: 'Спроектировать корпус вокруг реальной платы.',
            theory: [
                'Собираем всё вместе: коробка, стойки под плату, окно под '
                + 'дисплей и крышка с зазором.',
                'Порядок правильный такой: сначала измерь плату, потом добавь '
                + 'по 3 мм с каждой стороны на стенки и провода — и только '
                + 'потом строй коробку.',
                'Окно под дисплей делают вырезом по размерам экрана плюс '
                + 'небольшой зазор, чтобы дисплей встал без усилия.',
            ],
            steps: [
                'Построй коробку 76 × 56 × 24 мм со стенкой 2 мм',
                'Добавь четыре стойки под плату 70 × 50 мм',
                'Сделай окно под дисплей 30,4 × 14,4 мм в верхней грани',
                'Проверь работу',
            ],
            task: 'Корпус: коробка 76 × 56 × 24 мм со стенкой 2 мм, четыре '
                + 'стойки под плату и окно под дисплей 30,4 × 14,4 мм.',
            criteria: [
                { kind: 'size', value: [76, 56, 24], tolerance: 0.8,
                    label: 'Наружные габариты' },
                { kind: 'feature', value: ['shell'], label: 'Оболочка' },
                { kind: 'bodyCount', value: 1, label: 'Одно тело' },
            ],
            hints: [
                'Оболочку делают до стоек: иначе она срежет и стойки тоже.',
                'В каком порядке строить: сначала стойки или сначала оболочку?',
                'Сначала коробка и оболочка, потом стойки на дне, в конце '
                + 'окно под дисплей.',
            ],
        },

        {
            id: 'as-24-print',
            title: 'Проверка перед печатью',
            goal: 'Находить в детали то, что не напечатается.',
            theory: [
                'Принтер кладёт пластик снизу вверх слой за слоем. Не всё, '
                + 'что рисуется на экране, можно напечатать.',
                'Первое — толщина стенок. Тоньше 1,5 мм печатать нельзя: '
                + 'стенка получится хрупкой и сломается в руках.',
                'Второе — нависания. Если часть детали висит в воздухе, класть '
                + 'пластик не на что. Спасает скос: наклон до 45 градусов '
                + 'печатается сам, без поддержек.',
            ],
            steps: [
                'Построй коробку 50 × 50 × 30 мм со стенкой 2 мм',
                'Посмотри на неё сбоку — есть ли нависания?',
                'Проверь, что все стенки толще 1,5 мм',
                'Проверь работу',
            ],
            task: 'Коробка 50 × 50 × 30 мм со стенкой ровно 2 мм — такую '
                + 'напечатает любой принтер без поддержек.',
            blueprint: {
                front: {
                    w: 50,
                    h: 30,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 50, h: 30 },
                        { type: 'line', x1: 2, y1: 0, x2: 2, y2: 28 },
                        { type: 'line', x1: 48, y1: 0, x2: 48, y2: 28 },
                        { type: 'line', x1: 2, y1: 28, x2: 48, y2: 28 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 38, x2: 50, y2: 38, from: 30, label: '50' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 30, from: 0, label: '30' },
                    ]
                },
                side: {
                    w: 50,
                    h: 30,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 50, h: 30 },
                        { type: 'line', x1: 2, y1: 0, x2: 2, y2: 28 },
                        { type: 'line', x1: 48, y1: 0, x2: 48, y2: 28 },
                        { type: 'line', x1: 2, y1: 28, x2: 48, y2: 28 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 38, x2: 50, y2: 38, from: 30, label: '50' },
                        { orient: 'h', x1: 0, y1: 46, x2: 2, y2: 46, from: 50, label: '2' },
                    ]
                },
                top: {
                    w: 50,
                    h: 50,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 50, h: 50 },
                        { type: 'rect', x: 2, y: 2, w: 46, h: 46 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 58, x2: 50, y2: 58, from: 50, label: '50' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 50, from: 0, label: '50' },
                    ]
                },
                note: 'Стенка ровно 2 мм — это 5 линий сопла 0,4 мм. Такую стенку '
                    + 'принтер выкладывает целиком периметрами, без заполнения: '
                    + 'получается прочно и предсказуемо.'
            },
            criteria: [
                { kind: 'size', value: [50, 50, 30], tolerance: 0.5,
                    label: 'Наружные габариты' },
                { kind: 'feature', value: ['shell'], label: 'Оболочка' },
                { kind: 'volume', value: 50 * 50 * 30 - 46 * 46 * 28,
                    tolerance: 0.08, label: 'Толщина стенки 2 мм',
                    hint: 'Объём не сходится — проверь толщину стенки, '
                        + 'нужно ровно 2 мм.' },
            ],
            hints: [
                'Толщину стенки задают в свойствах оболочки.',
                'Почему стенку 1 мм печатать нельзя?',
                'Сопло принтера обычно 0,4 мм: стенка 1 мм — это всего два '
                + 'прохода, она ломается. От 1,5 мм уже надёжно.',
            ],
        },
    ],
};


/*
 * Курс 4. «Тела вращения и массивы».
 *
 * Формы, которые не сделать выдавливанием: воронка, шестерёнка, пружина.
 *
 * ⚠️ ПРОВЕРКА КРУГЛЫХ ДЕТАЛЕЙ. Габариты (bbox) у тела вращения работают
 * как обычно: цилиндр Ø60 даёт 60 × 60 × h. А вот объём считать по формуле
 * бруска нельзя — у конуса и спирали он совсем другой. Поэтому здесь
 * основные критерии: size (габариты) и feature (нужная операция),
 * а volume — только там, где форма простая и считается точно.
 */
const COURSE_REVOLVE = {
    id: 'revolve',
    title: 'Тела вращения и массивы',
    subtitle: 'Круглые детали и механизмы',
    description: 'Научимся делать формы, которые не получить выдавливанием: '
        + 'воронку, шестерёнку, пружину. И соберём механизм, который крутится.',
    lessons: [

        {
            id: 'rv-25-revolve',
            title: 'Вращение профиля',
            goal: 'Получать круглые детали вращением половины сечения.',
            theory: [
                'Выдавливание тянет фигуру прямо, поэтому чашку или воронку '
                + 'им не сделать: у них круглое сечение, которое меняется '
                + 'по высоте.',
                'Для таких форм есть вращение: рисуют половину профиля рядом '
                + 'с осью, и он обходит её полный круг.',
                'Главное правило: профиль не должен пересекать ось. Если '
                + 'перечеркнёт — деталь вывернется сама в себя и операция '
                + 'не выполнится.',
            ],
            steps: [
                'Создай эскиз на плоскости XZ',
                'Нарисуй профиль сбоку от оси',
                'Закрой эскиз и нажми «Вращение»',
                'Задай угол 360 градусов',
            ],
            task: 'Построй вращением цилиндрический стакан: наружный диаметр '
                + '50 мм, высота 60 мм, толщина стенки 3 мм.',
            blueprint: {
                front: {
                    w: 50,
                    h: 60,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 50, h: 60 },
                        { type: 'line', x1: 3, y1: 0, x2: 3, y2: 57 },
                        { type: 'line', x1: 47, y1: 0, x2: 47, y2: 57 },
                        { type: 'line', x1: 3, y1: 57, x2: 47, y2: 57 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 68, x2: 50, y2: 68, from: 60, label: '⊘50' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 60, from: 0, label: '60' },
                        { orient: 'h', x1: 0, y1: -8, x2: 3, y2: -8, from: 0, label: '3' },
                    ]
                },
                top: {
                    w: 50,
                    h: 50,
                    shapes: [
                        { type: 'circle', cx: 25, cy: 25, r: 25 },
                        { type: 'circle', cx: 25, cy: 25, r: 22 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 58, x2: 50, y2: 58, from: 50, label: '⊘50' },
                    ]
                },
                note: 'Стакан получается вращением ПЛОСКОГО профиля вокруг оси. '
                    + 'На виде спереди этот профиль и виден: буква «П» шириной 3 '
                    + 'мм — стенка и дно.'
            },
            criteria: [
                { kind: 'size', value: [50, 50, 60], tolerance: 1,
                    label: 'Габариты стакана' },
                { kind: 'feature', value: ['revolve'], label: 'Вращение' },
                { kind: 'bodyCount', value: 1, label: 'Одно тело' },
            ],
            hints: [
                'Профиль стакана — буква П, положенная на бок: дно, стенка '
                + 'и внутренняя поверхность.',
                'На каком расстоянии от оси начинается профиль стакана?',
                'Внутренняя стенка — на 22 мм от оси, наружная на 25. '
                + 'Разница 3 мм и есть толщина.',
            ],
        },

        {
            id: 'rv-26-funnel',
            title: 'Воронка',
            goal: 'Сделать воронку с носиком и скруглением на переходе.',
            theory: [
                'Классическая деталь: широкий конус, узкий носик и скругление '
                + 'на стыке, чтобы жидкость не застревала в углу.',
                'Профиль воронки — ломаная линия: наклонная стенка конуса, '
                + 'потом вертикальный носик.',
                'Скругление на переходе делают уже на готовом теле, обычным '
                + 'инструментом «Скругление».',
            ],
            steps: [
                'Нарисуй профиль: наклонная стенка и носик',
                'Вращай на 360 градусов',
                'Скругли переход между конусом и носиком',
                'Проверь толщину стенки — не меньше 1,5 мм',
            ],
            task: 'Воронка: верхний диаметр 60 мм, высота 50 мм, носик '
                + 'диаметром 12 мм. Переход между конусом и носиком скруглён.',
            blueprint: {
                front: {
                    w: 60,
                    h: 50,
                    shapes: [
                        { type: 'line', x1: 0, y1: 50, x2: 24, y2: 12 },
                        { type: 'line', x1: 60, y1: 50, x2: 36, y2: 12 },
                        { type: 'line', x1: 0, y1: 50, x2: 60, y2: 50 },
                        { type: 'rect', x: 24, y: 0, w: 12, h: 12 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 58, x2: 60, y2: 58, from: 50, label: '⊘60' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 50, from: 0, label: '50' },
                        { orient: 'h', x1: 24, y1: -8, x2: 36, y2: -8, from: 0, label: '⊘12' },
                    ]
                },
                top: {
                    w: 60,
                    h: 60,
                    shapes: [
                        { type: 'circle', cx: 30, cy: 30, r: 30 },
                        { type: 'circle', cx: 30, cy: 30, r: 6 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 68, x2: 60, y2: 68, from: 60, label: '⊘60' },
                    ]
                },
                note: 'Профиль для вращения — ломаная: наклонная стенка конуса '
                    + 'переходит в прямой носик. Переход скругляют, иначе в углу '
                    + 'застревает то, что через воронку льют.'
            },
            criteria: [
                { kind: 'size', value: [60, 60, 50], tolerance: 1.5,
                    label: 'Габариты воронки' },
                { kind: 'feature', value: ['revolve', 'fillet'],
                    label: 'Вращение и скругление' },
            ],
            hints: [
                'Носик — это просто продолжение профиля вниз, вертикальной '
                + 'линией.',
                'Что произойдёт с жидкостью, если переход оставить острым?',
                'В остром углу жидкость застревает. Скругли переход '
                + 'радиусом 3 мм.',
            ],
        },

        {
            id: 'rv-27-pattern',
            title: 'Круговой массив',
            goal: 'Размножать элемент по кругу одной операцией.',
            theory: [
                'Двадцать отверстий по кругу вручную — двадцать раз считать '
                + 'координаты и всё равно ошибиться.',
                'Круговой массив делает это сам: строишь ОДИН элемент, '
                + 'указываешь ось и число копий — редактор расставляет их '
                + 'по окружности с равным шагом.',
                'Главная выгода не в скорости, а в правках: поменяешь '
                + 'образец — изменятся все копии сразу.',
            ],
            steps: [
                'Построй диск Ø80 мм высотой 8 мм',
                'Сделай одно отверстие Ø6 мм на расстоянии 28 мм от центра',
                'Примени круговой массив: ось — центр диска, копий 8',
                'Проверь работу',
            ],
            task: 'Диск Ø80 мм высотой 8 мм с восемью отверстиями Ø6 мм, '
                + 'расставленными по кругу на радиусе 28 мм.',
            blueprint: {
                front: {
                    w: 80,
                    h: 8,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 80, h: 8 },
                        { type: 'line', x1: 9, y1: 0, x2: 9, y2: 8, hidden: true },
                        { type: 'line', x1: 15, y1: 0, x2: 15, y2: 8, hidden: true },
                        { type: 'line', x1: 65, y1: 0, x2: 65, y2: 8, hidden: true },
                        { type: 'line', x1: 71, y1: 0, x2: 71, y2: 8, hidden: true },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 16, x2: 80, y2: 16, from: 8, label: '⊘80' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 8, from: 0, label: '8' },
                    ]
                },
                top: {
                    w: 80,
                    h: 80,
                    shapes: [
                        { type: 'circle', cx: 40, cy: 40, r: 40 },
                        { type: 'circle', cx: 68, cy: 40, r: 3 },
                        { type: 'circle', cx: 59.799, cy: 59.799, r: 3 },
                        { type: 'circle', cx: 40, cy: 68, r: 3 },
                        { type: 'circle', cx: 20.201, cy: 59.799, r: 3 },
                        { type: 'circle', cx: 12, cy: 40, r: 3 },
                        { type: 'circle', cx: 20.201, cy: 20.201, r: 3 },
                        { type: 'circle', cx: 40, cy: 12, r: 3 },
                        { type: 'circle', cx: 59.799, cy: 20.201, r: 3 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 88, x2: 80, y2: 88, from: 80, label: '⊘80' },
                        { orient: 'h', x1: 40, y1: 96, x2: 68, y2: 96, from: 88, label: 'R28' },
                        { orient: 'h', x1: 65, y1: 104, x2: 71, y2: 104, from: 96, label: '⊘6' },
                    ]
                },
                note: 'Восемь отверстий ⊘6 мм на окружности радиусом 28 мм — то '
                    + 'есть через каждые 45°. Такое повторение по кругу и делает '
                    + 'круговой массив: рисуешь одно отверстие, остальные считает '
                    + 'он.'
            },
            criteria: [
                { kind: 'size', value: [80, 80, 8], tolerance: 1,
                    label: 'Габариты диска' },
                /* Объём надёжнее счёта отверстий: диск сам цилиндр, и его
                   боковая поверхность тоже цилиндрическая грань. Объём же
                   прямо показывает, что материал вынут восемь раз. */
                /* ⚠️ Допуск 2%, а не обычные 5–6%: восемь отверстий Ø6 мм
                   вынимают всего 4,5% объёма диска. При допуске 6% критерий
                   прошёл бы и у детали БЕЗ единого отверстия — то есть
                   не проверял бы ничего. */
                { kind: 'volume',
                    value: Math.PI * 40 * 40 * 8 - 8 * Math.PI * 3 * 3 * 8,
                    tolerance: 0.02, label: 'Объём с отверстиями',
                    hint: 'Отверстий должно быть восемь, диаметром 6 мм.' },
                { kind: 'bodyCount', value: 1, label: 'Одно тело' },
            ],
            hints: [
                'Массив применяется к уже готовому отверстию, а не рисуется '
                + 'заново.',
                'Что нужно указать массиву, кроме числа копий?',
                'Ось вращения — она проходит через центр диска вертикально.',
            ],
        },

        {
            id: 'rv-28-gear',
            title: 'Шестерёнка',
            goal: 'Построить шестерёнку массивом зубьев.',
            theory: [
                'Шестерёнка — круговой массив в чистом виде: один зуб, '
                + 'размноженный по кругу, и отверстие под вал в центре.',
                'Число зубьев связано с диаметром через модуль: диаметр '
                + 'делить на число зубьев. У сцепляющихся шестерён модуль '
                + 'должен совпадать, иначе зубья не войдут друг в друга.',
                'Начинают всегда с диска-заготовки, потом добавляют один зуб '
                + 'и размножают его.',
            ],
            steps: [
                'Построй диск Ø60 мм высотой 10 мм',
                'Нарисуй один зуб на краю диска',
                'Размножь его круговым массивом на 12 копий',
                'Сделай отверстие Ø10 мм под вал',
            ],
            task: 'Шестерёнка: диск Ø60 мм высотой 10 мм, 12 зубьев '
                + 'по кругу, отверстие Ø10 мм под вал в центре.',
            blueprint: {
                front: {
                    w: 60,
                    h: 10,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 60, h: 10 },
                        { type: 'line', x1: 25, y1: 0, x2: 25, y2: 10, hidden: true },
                        { type: 'line', x1: 35, y1: 0, x2: 35, y2: 10, hidden: true },
                    ],
                    dims: [
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 10, from: 0, label: '10' },
                    ]
                },
                top: {
                    w: 60,
                    h: 60,
                    shapes: [
                        { type: 'circle', cx: 30, cy: 30, r: 30 },
                        { type: 'circle', cx: 30, cy: 30, r: 25.8 },
                        { type: 'circle', cx: 30, cy: 30, r: 5 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 68, x2: 60, y2: 68, from: 60, label: '⊘60' },
                        { orient: 'h', x1: 25, y1: 76, x2: 35, y2: 76, from: 68, label: '⊘10' },
                    ]
                },
                note: 'Внешняя окружность — вершины зубьев ⊘60 мм, внутренняя — '
                    + 'их основания. Двенадцать зубьев расставлены круговым '
                    + 'массивом через 30°. В центре отверстие ⊘10 мм под вал.'
            },
            criteria: [
                { kind: 'size', value: [60, 60, 10], tolerance: 3,
                    label: 'Габариты шестерёнки',
                    hint: 'Габариты считаются по вершинам зубьев — они могут '
                        + 'быть чуть больше диска.' },
                /* Отверстие под вал числом не проверяем: сам диск — цилиндр,
                   и его боковая поверхность тоже цилиндрическая грань.
                   Габариты с допуском на зубья надёжнее. */
                { kind: 'bodyCount', value: 1, label: 'Одно тело' },
                /* Главный приём урока — размножить зуб массивом, а не рисовать
                   12 штук вручную. Без этого критерия шаг 3 не проверялся. */
                { kind: 'feature', value: ['circPattern'], label: 'Зубья массивом',
                    hint: 'Зубья нужно размножить круговым массивом на 12 копий, '
                        + 'а не рисовать каждый отдельно.' },
            ],
            hints: [
                'Зуб проще всего нарисовать трапецией на краю диска.',
                'Как посчитать модуль этой шестерёнки?',
                'Модуль = 60 разделить на 12 = 5. У парной шестерёнки '
                + 'он должен быть таким же.',
            ],
        },

        {
            id: 'rv-29-gear-pair',
            title: 'Пара шестерёнок',
            goal: 'Понять передаточное отношение и зацепление.',
            theory: [
                'Одна шестерёнка ничего не делает. Работают они парами: одна '
                + 'ведущая, вторая ведомая. Зубья входят друг в друга '
                + 'и передают вращение.',
                'Если у первой 12 зубьев, а у второй 24, то вторая крутится '
                + 'вдвое медленнее — зато вдвое сильнее. Это передаточное '
                + 'отношение.',
                'Расстояние между центрами — сумма радиусов. Поставишь ближе '
                + 'или дальше — зубья не зацепятся.',
            ],
            steps: [
                'Построй шестерёнку Ø50 мм с 10 зубьями',
                'Построй вторую Ø100 мм с 20 зубьями',
                'Поставь их так, чтобы расстояние между центрами было 75 мм',
                'Проверь: получилось два тела',
            ],
            task: 'Две шестерёнки одного модуля: Ø50 мм с 10 зубьями '
                + 'и Ø100 мм с 20 зубьями. Должно получиться два тела.',
            blueprint: {
                front: {
                    w: 150,
                    h: 10,
                    shapes: [
                        { type: 'label', x: 25, y: -8, text: 'Деталь 1 — малая ⊘50' },
                        { type: 'rect', x: 0, y: 0, w: 50, h: 10 },
                        { type: 'label', x: 100, y: -8, text: 'Деталь 2 — большая ⊘100' },
                        { type: 'rect', x: 50, y: 0, w: 100, h: 10 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 18, x2: 50, y2: 18, from: 10, label: '⊘50' },
                        { orient: 'h', x1: 50, y1: 28, x2: 150, y2: 28, from: 10, label: '⊘100' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 10, from: 0, label: '10' },
                    ]
                },
                top: {
                    w: 150,
                    h: 100,
                    shapes: [
                        { type: 'label', x: 25, y: -8, text: 'Деталь 1 — 10 зубьев' },
                        { type: 'circle', cx: 25, cy: 50, r: 25 },
                        { type: 'label', x: 100, y: -8, text: 'Деталь 2 — 20 зубьев' },
                        { type: 'circle', cx: 25, cy: 50, r: 21 },
                        { type: 'circle', cx: 100, cy: 50, r: 50 },
                        { type: 'circle', cx: 100, cy: 50, r: 46 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 108, x2: 50, y2: 108, from: 100, label: '⊘50' },
                        { orient: 'h', x1: 50, y1: 120, x2: 150, y2: 120, from: 100, label: '⊘100' },
                        { orient: 'h', x1: 25, y1: 134, x2: 100, y2: 134, from: 100, label: '75' },
                    ]
                },
                note: 'Диаметры относятся как 1 : 2, и зубьев ровно вдвое больше '
                    + '— это и значит «один модуль». Расстояние между осями 75 мм '
                    + '— сумма радиусов: только так зубья войдут друг в друга.'
            },
            criteria: [
                { kind: 'bodyCount', value: 2, label: 'Две шестерёнки' },
                { kind: 'holes', value: 2, label: 'Отверстия под валы' },
            ],
            hints: [
                'Модуль у обеих должен совпадать: 50 на 10 и 100 на 20 — '
                + 'это одно и то же число.',
                'Во сколько раз медленнее будет крутиться большая?',
                'В два раза: у неё вдвое больше зубьев. Зато момент '
                + 'вдвое выше.',
            ],
        },

        {
            id: 'rv-30-coil',
            title: 'Спираль',
            goal: 'Строить пружину: шаг, витки, толщина проволоки.',
            theory: [
                'Пружина — тоже тело вращения, но особое: профиль не просто '
                + 'обходит ось, а поднимается по ней. Такая линия называется '
                + 'спиралью.',
                'У спирали три числа: шаг — на сколько поднимается за один '
                + 'оборот, число витков и толщина проволоки.',
                'Чем тоньше проволока и больше витков, тем мягче пружина. '
                + 'Толстая проволока и мало витков — жёсткая.',
            ],
            steps: [
                'Возьми инструмент «Спираль»',
                'Задай диаметр 30 мм и толщину проволоки 3 мм',
                'Задай шаг 8 мм и 6 витков',
                'Проверь высоту: 6 витков по 8 мм — это 48 мм',
            ],
            task: 'Пружина: диаметр 30 мм, проволока Ø3 мм, шаг 8 мм, '
                + '6 витков.',
            blueprint: {
                front: {
                    w: 30,
                    h: 48,
                    shapes: [
                        { type: 'circle', cx: 1.5, cy: 4, r: 1.5 },
                        { type: 'circle', cx: 1.5, cy: 12, r: 1.5 },
                        { type: 'circle', cx: 1.5, cy: 20, r: 1.5 },
                        { type: 'circle', cx: 1.5, cy: 28, r: 1.5 },
                        { type: 'circle', cx: 1.5, cy: 36, r: 1.5 },
                        { type: 'circle', cx: 1.5, cy: 44, r: 1.5 },
                        { type: 'circle', cx: 28.5, cy: 8, r: 1.5 },
                        { type: 'circle', cx: 28.5, cy: 16, r: 1.5 },
                        { type: 'circle', cx: 28.5, cy: 24, r: 1.5 },
                        { type: 'circle', cx: 28.5, cy: 32, r: 1.5 },
                        { type: 'circle', cx: 28.5, cy: 40, r: 1.5 },
                        { type: 'circle', cx: 28.5, cy: 48, r: 1.5 },
                        { type: 'line', x1: 1.5, y1: 4, x2: 28.5, y2: 8 },
                        { type: 'line', x1: 1.5, y1: 12, x2: 28.5, y2: 16 },
                        { type: 'line', x1: 1.5, y1: 20, x2: 28.5, y2: 24 },
                        { type: 'line', x1: 1.5, y1: 28, x2: 28.5, y2: 32 },
                        { type: 'line', x1: 1.5, y1: 36, x2: 28.5, y2: 40 },
                        { type: 'line', x1: 1.5, y1: 44, x2: 28.5, y2: 48 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 56, x2: 30, y2: 56, from: 48, label: '⊘30' },
                        { orient: 'v', x1: -8, y1: 4, x2: -8, y2: 12, from: 0, label: '8' },
                        { orient: 'v', x1: -16, y1: 0, x2: -16, y2: 48, from: -8, label: '48' },
                    ]
                },
                top: {
                    w: 30,
                    h: 30,
                    shapes: [
                        { type: 'circle', cx: 15, cy: 15, r: 15 },
                        { type: 'circle', cx: 15, cy: 15, r: 12 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 38, x2: 30, y2: 38, from: 30, label: '⊘30' },
                    ]
                },
                note: 'Пружина — это сечение ⊘3 мм, проведённое по винтовой '
                    + 'линии. Шаг 8 мм — на столько поднимается виток за оборот; '
                    + 'шесть витков дают высоту 48 мм.'
            },
            criteria: [
                { kind: 'size', value: [30, 30, 48], tolerance: 4,
                    label: 'Габариты пружины',
                    hint: 'Высота = шаг × число витков. При шаге 8 и шести '
                        + 'витках получается 48 мм.' },
                { kind: 'bodyCount', value: 1, label: 'Одно тело' },
                /* Урок именно про инструмент «Спираль»: без этого критерия
                   засчитывался бы любой цилиндр подходящих габаритов. */
                { kind: 'feature', value: ['coil'], label: 'Сделано спиралью',
                    hint: 'Пружину строят инструментом «Спираль» — '
                        + 'задай диаметр, шаг и число витков.' },
            ],
            hints: [
                'Высота пружины считается сама: шаг умножить на число витков.',
                'Что изменится, если увеличить шаг вдвое?',
                'Пружина станет вдвое выше и мягче: витки разойдутся дальше '
                + 'друг от друга.',
            ],
        },

        {
            id: 'rv-31-damper',
            title: 'Амортизатор',
            goal: 'Собрать узел из пружины и опор.',
            theory: [
                'Амортизатор — пружина между двумя опорами. Пружина сжимается '
                + 'и гасит удар, опоры держат её на месте.',
                'Опора — диск с бортиком, в который садится пружина. Бортик '
                + 'не даёт ей уехать вбок.',
                'Диаметр пружины должен быть чуть меньше внутреннего диаметра '
                + 'бортика — тот самый зазор из третьего курса.',
            ],
            steps: [
                'Построй опору: диск Ø40 мм высотой 6 мм с бортиком',
                'Построй пружину Ø32 мм',
                'Проверь зазор между пружиной и бортиком',
                'Проверь: получилось два тела',
            ],
            task: 'Амортизатор: опора Ø40 мм высотой 6 мм и пружина Ø32 мм '
                + 'с 6 витками. Два отдельных тела.',
            blueprint: {
                front: {
                    w: 40,
                    h: 54,
                    shapes: [
                        { type: 'label', x: 20, y: -8, text: 'Деталь 1 — опора, деталь 2 — пружина' },
                        { type: 'rect', x: 0, y: 0, w: 40, h: 6 },
                        { type: 'line', x1: 4, y1: 6, x2: 36, y2: 10 },
                        { type: 'line', x1: 4, y1: 14, x2: 36, y2: 18 },
                        { type: 'line', x1: 4, y1: 22, x2: 36, y2: 26 },
                        { type: 'line', x1: 4, y1: 30, x2: 36, y2: 34 },
                        { type: 'line', x1: 4, y1: 38, x2: 36, y2: 42 },
                        { type: 'line', x1: 4, y1: 46, x2: 36, y2: 50 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 62, x2: 40, y2: 62, from: 54, label: '⊘40' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 6, from: 0, label: '6' },
                    ]
                },
                top: {
                    w: 40,
                    h: 40,
                    shapes: [
                        { type: 'circle', cx: 20, cy: 20, r: 20 },
                        { type: 'circle', cx: 20, cy: 20, r: 16 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 48, x2: 40, y2: 48, from: 40, label: '⊘40' },
                        { orient: 'h', x1: 4, y1: 56, x2: 36, y2: 56, from: 48, label: '⊘32' },
                    ]
                },
                note: 'Две отдельные детали: опора-шайба ⊘40 × 6 мм и пружина ⊘32 '
                    + 'мм на ней. Пружина стоит НА опоре, а не внутри неё — тела '
                    + 'не должны пересекаться.'
            },
            criteria: [
                { kind: 'bodyCount', value: 2, label: 'Опора и пружина' },
                { kind: 'gap', min: 0.1, max: 4, label: 'Зазор в бортике',
                    hint: 'Пружина должна входить в бортик свободно, '
                        + 'но не болтаться.' },
            ],
            hints: [
                'Опора и пружина — отдельные тела: при построении выбирай '
                + '«Новое тело».',
                'Почему пружина не может быть точно такого же диаметра, '
                + 'как бортик?',
                'Она не влезет: нужен зазор, как и в любой другой сборке.',
            ],
        },

        {
            id: 'rv-32-thread',
            title: 'Резьба',
            goal: 'Понимать, как устроена резьба и когда её печатать.',
            theory: [
                'Резьба — это спираль, только не пружина, а канавка '
                + 'на цилиндре. По ней винт вкручивается в гайку.',
                'У резьбы есть шаг — на сколько винт продвинется за один '
                + 'оборот. У М3 шаг полмиллиметра: десять оборотов дают '
                + 'пять миллиметров хода.',
                'Печатать мелкую резьбу трудно: витки смазываются. В печатных '
                + 'деталях чаще ставят обычный винт с гайкой, а резьбу '
                + 'крупнее М8 печатают сносно.',
            ],
            steps: [
                'Построй цилиндр Ø12 мм высотой 40 мм',
                'Возьми инструмент «Резьба» и выбери боковую поверхность',
                'Задай размер М12',
                'Подумай: напечатается ли такая резьба?',
            ],
            task: 'Винт: цилиндр Ø12 мм высотой 40 мм с резьбой М12 '
                + 'на боковой поверхности.',
            blueprint: {
                front: {
                    w: 12,
                    h: 40,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 12, h: 40 },
                        { type: 'line', x1: 0, y1: 2, x2: 12, y2: 3.75 },
                        { type: 'line', x1: 0, y1: 5.5, x2: 12, y2: 7.25 },
                        { type: 'line', x1: 0, y1: 9, x2: 12, y2: 10.75 },
                        { type: 'line', x1: 0, y1: 12.5, x2: 12, y2: 14.25 },
                        { type: 'line', x1: 0, y1: 16, x2: 12, y2: 17.75 },
                        { type: 'line', x1: 0, y1: 19.5, x2: 12, y2: 21.25 },
                        { type: 'line', x1: 0, y1: 23, x2: 12, y2: 24.75 },
                        { type: 'line', x1: 0, y1: 26.5, x2: 12, y2: 28.25 },
                        { type: 'line', x1: 0, y1: 30, x2: 12, y2: 31.75 },
                        { type: 'line', x1: 0, y1: 33.5, x2: 12, y2: 35.25 },
                        { type: 'line', x1: 0, y1: 37, x2: 12, y2: 38.75 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 48, x2: 12, y2: 48, from: 40, label: 'М12' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 40, from: 0, label: '40' },
                        { orient: 'v', x1: 20, y1: 2, x2: 20, y2: 5.5, from: 12, label: '1,75' },
                    ]
                },
                top: {
                    w: 12,
                    h: 12,
                    shapes: [
                        { type: 'circle', cx: 6, cy: 6, r: 6 },
                        { type: 'circle', cx: 6, cy: 6, r: 4.8 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 20, x2: 12, y2: 20, from: 12, label: '⊘12' },
                    ]
                },
                note: 'М12 — резьба наружным диаметром 12 мм с шагом 1,75 мм. На '
                    + 'чертеже её показывают двумя окружностями: сплошной по '
                    + 'вершинам и тонкой по впадинам.'
            },
            criteria: [
                /*
                 * ⚠️ САМУ РЕЗЬБУ ПРОВЕРИТЬ НЕЧЕМ — инструмента нет.
                 *
                 * «Резьба» в TopBar помечена disabled: true, то есть в
                 * редакторе она не реализована. Критерий на неё заблокировал
                 * бы урок наглухо: выполнить его было бы невозможно.
                 * Поэтому проверяем то, что ученик реально может построить
                 * (цилиндр Ø12 × 40), а сама резьба остаётся теоретической
                 * частью урока — о ней там и рассказывают.
                 *
                 * Когда инструмент появится, сюда добавляется
                 * { kind: 'feature', value: ['thread'] } и правится задание.
                 */
                { kind: 'size', value: [12, 12, 40], tolerance: 1.5,
                    label: 'Габариты винта' },
                { kind: 'bodyCount', value: 1, label: 'Одно тело' },
            ],
            hints: [
                'Резьба делается на готовом цилиндре, а не рисуется эскизом.',
                'Почему резьбу М3 печатать бессмысленно?',
                'Шаг М3 — полмиллиметра, а сопло принтера 0,4 мм. Витки '
                + 'сольются в гладкий цилиндр.',
            ],
        },

        {
            id: 'rv-33-sweep',
            title: 'Протяжка по траектории',
            goal: 'Делать ручки и скобы: сечение вдоль кривой.',
            theory: [
                'Третий способ получить тело: провести сечение вдоль линии. '
                + 'Так делают ручки, скобы, трубы — всё, что тянется '
                + 'по кривой.',
                'Нужны два эскиза: сечение — что тянем, и траектория — куда. '
                + 'Они лежат на разных плоскостях.',
                'Сечение обычно круг или прямоугольник, траектория — ломаная '
                + 'или дуга.',
            ],
            steps: [
                'Нарисуй траекторию на плоскости XZ: дугу или ломаную',
                'Нарисуй сечение на плоскости XY: круг Ø8 мм',
                'Возьми «Сдвиг по траектории»',
                'Укажи сечение и путь',
            ],
            task: 'Ручка: круглое сечение Ø8 мм, проведённое по П-образной '
                + 'траектории высотой 40 мм и шириной 60 мм.',
            blueprint: {
                front: {
                    w: 60,
                    h: 40,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 8, h: 40 },
                        { type: 'rect', x: 52, y: 0, w: 8, h: 40 },
                        { type: 'rect', x: 0, y: 32, w: 60, h: 8 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 48, x2: 60, y2: 48, from: 40, label: '60' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 40, from: 0, label: '40' },
                        { orient: 'h', x1: 0, y1: -8, x2: 8, y2: -8, from: 0, label: '⊘8' },
                    ]
                },
                side: {
                    w: 8,
                    h: 40,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 8, h: 40 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 48, x2: 8, y2: 48, from: 40, label: '⊘8' },
                    ]
                },
                note: 'Ручка — круг ⊘8 мм, проведённый по П-образной траектории. '
                    + 'Сама траектория на чертеже не видна: видно тело, которое '
                    + 'получилось. Углы скругляют, иначе сечение в повороте '
                    + 'сминается.'
            },
            criteria: [
                { kind: 'size', value: [60, 8, 40], tolerance: 4,
                    label: 'Габариты ручки' },
                { kind: 'bodyCount', value: 1, label: 'Одно тело' },
                /* Ровно тот приём, которому учит урок: сечение + путь.
                   Без критерия проходила бы и П-образная деталь из трёх
                   выдавленных брусков. */
                { kind: 'feature', value: ['sweep'], label: 'Сдвиг по траектории',
                    hint: 'Ручку строят инструментом «Сдвиг по траектории»: '
                        + 'сечение Ø8 мм ведут по П-образному пути.' },
            ],
            hints: [
                'Траектория и сечение должны быть на РАЗНЫХ плоскостях, '
                + 'иначе протяжка не получится.',
                'Где должно начинаться сечение относительно траектории?',
                'Сечение ставят в начале траектории, перпендикулярно ей.',
            ],
        },

        {
            id: 'rv-34-mechanism',
            title: 'Проект: механизм из двух деталей',
            goal: 'Собрать то, что реально крутится.',
            theory: [
                'Итог курса: механизм, в котором что-то вращается. Две '
                + 'шестерёнки на валах — самое понятное.',
                'Здесь сходятся все курсы: тела вращения из четвёртого, '
                + 'зазоры из третьего, чтение чертежа из второго.',
                'Шестерёнка садится на вал с плотной посадкой, чтобы '
                + 'не проворачивалась: отверстие на 0,1 мм больше вала.',
            ],
            steps: [
                'Построй вал Ø10 мм длиной 50 мм',
                'Построй шестерёнку с отверстием Ø10,1 мм',
                'Проверь, что шестерёнка садится на вал',
                'Проверь: получилось два тела',
            ],
            task: 'Механизм: вал Ø10 мм длиной 50 мм и шестерёнка Ø56 мм '
                + 'с 16 зубьями, отверстие под вал Ø10,1 мм. Два тела.',
            blueprint: {
                front: {
                    w: 106,
                    h: 50,
                    shapes: [
                        { type: 'label', x: 25, y: 12, text: 'Деталь 1 — вал' },
                        { type: 'rect', x: 0, y: 20, w: 50, h: 10 },
                        { type: 'line', x1: 55, y1: -6, x2: 55, y2: 46, hidden: true },
                        { type: 'label', x: 83, y: -8, text: 'Деталь 2 — шестерёнка' },
                        { type: 'rect', x: 60, y: 0, w: 46, h: 10 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 58, x2: 50, y2: 58, from: 30, label: '50' },
                        { orient: 'v', x1: -8, y1: 20, x2: -8, y2: 30, from: 0, label: '⊘10' },
                    ]
                },
                top: {
                    w: 106,
                    h: 56,
                    shapes: [
                        { type: 'label', x: 78, y: -8, text: 'Деталь 2 — шестерёнка ⊘56' },
                        { type: 'circle', cx: 78, cy: 28, r: 28 },
                        { type: 'circle', cx: 78, cy: 28, r: 24 },
                        { type: 'circle', cx: 78, cy: 28, r: 5.05 },
                    ],
                    dims: [
                        { orient: 'h', x1: 50, y1: 64, x2: 106, y2: 64, from: 56, label: '⊘56' },
                        { orient: 'h', x1: 72.95, y1: 72, x2: 83.05, y2: 72, from: 64, label: '⊘10,1' },
                    ]
                },
                note: 'Вал ⊘10 мм и шестерёнка с отверстием ⊘10,1 мм — зазор 0,1 '
                    + 'мм на диаметр. Это плотная посадка: шестерёнка сядет на '
                    + 'вал от руки, но не будет болтаться.'
            },
            criteria: [
                { kind: 'bodyCount', value: 2, label: 'Вал и шестерёнка' },
                { kind: 'holes', value: 1, label: 'Отверстие под вал' },
            ],
            freeform: true,
            hints: [
                'Вал — тело вращения или простой цилиндр, шестерёнка — '
                + 'диск с массивом зубьев.',
                'Какая посадка нужна, чтобы шестерёнка не проворачивалась '
                + 'на валу?',
                'Плотная: отверстие всего на 0,1 мм больше вала. Свободная '
                + 'посадка позволила бы ей крутиться вхолостую.',
            ],
        },
    ],
};


/*
 * Курс 5. «Проектирование по заданию» — последний.
 *
 * Здесь ребёнок не повторяет, а решает. В заданиях НЕТ готовых размеров:
 * есть требования и ограничения, а числа он выводит сам — из измерений
 * реального предмета или из здравого смысла.
 *
 * ⚠️ ПРОВЕРКА ЗДЕСЬ ДРУГАЯ. Раз решение у каждого своё, критерии смотрят
 * не точный размер, а выполнение ТРЕБОВАНИЙ: не больше ограничения,
 * есть нужные операции, деталь одна или столько, сколько задано.
 * Часть уроков помечена freeform: там проверяются только общие требования,
 * остальное — на усмотрение автора.
 */
const COURSE_DESIGN = {
    id: 'design',
    title: 'Проектирование по заданию',
    subtitle: 'Решать, а не повторять',
    description: 'Настоящие инженерные задачи: требования вместо размеров, '
        + 'ограничения вместо инструкций. Здесь ты придумываешь сам.',
    lessons: [

        {
            id: 'pr-35-brief',
            title: 'Техническое задание',
            goal: 'Превращать пожелания в проверяемые требования.',
            theory: [
                'Работа инженера начинается не с эскиза, а с требований. '
                + 'Их называют техническим заданием.',
                'Главное правило: требование — это число, которое можно '
                + 'проверить линейкой. «Должно быть удобно» — пожелание. '
                + '«Ручка диаметром 25–30 мм» — требование.',
                'Прежде чем строить, выпиши все требования. По ним же потом '
                + 'себя и проверишь.',
            ],
            steps: [
                'Прочитай задание и выпиши требования',
                'Переведи каждое пожелание в число',
                'Построй деталь так, чтобы все числа сошлись',
                'Сверь готовое с выписанным списком',
            ],
            task: 'Подставка под телефон: высота не больше 100 мм, основание '
                + 'не меньше 70 мм в самом узком месте, все края скруглены. '
                + 'Точные размеры выбираешь сам.',
            criteria: [
                { kind: 'feature', value: ['fillet'], label: 'Края скруглены',
                    hint: 'Требование «не должно царапать» означает '
                        + 'скругления по краям.' },
                { kind: 'bodyCount', value: 1, label: 'Одна деталь' },
            ],
            freeform: true,
            hints: [
                'Требования задают рамки, а не точный размер: внутри рамок '
                + 'решаешь сам.',
                'Какое требование здесь про безопасность?',
                'Скругления: острый край царапает руки и провода. '
                + 'Радиус выбирай сам, от 2 мм.',
            ],
        },

        {
            id: 'pr-36-idea',
            title: 'От идеи к эскизу',
            goal: 'Продумывать деталь до того, как открыть редактор.',
            theory: [
                'Между «придумал» и «построил» есть шаг, который пропускают '
                + 'почти все: набросок на бумаге. Он экономит часы.',
                'На наброске проверяют главное: сколько деталей, как они '
                + 'держатся друг за друга, где что торчит. Стереть карандаш '
                + 'проще, чем переделывать модель.',
                'Каждую линию наброска нужно суметь назвать числом. Если '
                + 'не получается — идея ещё не додумана.',
            ],
            steps: [
                'Нарисуй набросок на бумаге',
                'Проставь на нём все размеры',
                'Только потом открывай редактор',
                'Построй по готовым числам',
            ],
            task: 'Крючок для одежды: крепится к стене двумя винтами М4, '
                + 'выступает от стены не больше чем на 60 мм, выдерживает '
                + 'куртку. Размеры — свои.',
            criteria: [
                { kind: 'holes', value: 2, label: 'Два отверстия под винты' },
                { kind: 'bodyCount', value: 1, label: 'Одна деталь' },
            ],
            freeform: true,
            hints: [
                'Отверстия под М4 — это 4,3 мм, помнишь из третьего курса.',
                'Почему крючок не может быть тонким у стены?',
                'Там самая большая нагрузка: у основания делают толще, '
                + 'к концу можно тоньше.',
            ],
        },

        {
            id: 'pr-37-holder',
            title: 'Держатель для наушников',
            goal: 'Сделать вещь по трём требованиям сразу.',
            theory: [
                'Держатель должен висеть на краю стола, не царапать провод '
                + 'и не падать, когда наушники висят.',
                'Первое число берут с реального стола: измерь толщину '
                + 'столешницы и добавь запас в полмиллиметра, чтобы '
                + 'держатель надевался.',
                'Профиль держателя — буква Г: вертикальная часть и крючок, '
                + 'который цепляется за стол.',
            ],
            steps: [
                'Измерь толщину своей столешницы',
                'Нарисуй Г-образный профиль с запасом 0,5 мм',
                'Выдави на ширину держателя',
                'Скругли все края',
            ],
            task: 'Держатель для наушников на край стола. Паз по толщине '
                + 'твоей столешницы плюс запас, все края скруглены.',
            criteria: [
                { kind: 'feature', value: ['fillet'], label: 'Края скруглены' },
                { kind: 'bodyCount', value: 1, label: 'Одна деталь' },
            ],
            freeform: true,
            hints: [
                'Г-образный профиль рисуют линиями на плоскости XZ — '
                + 'это вид сбоку.',
                'Что будет, если сделать паз ровно по толщине стола?',
                'Не наденется: нужен запас, как в любой посадке. '
                + 'Полмиллиметра достаточно.',
            ],
        },

        {
            id: 'pr-38-measure',
            title: 'Измерение реального предмета',
            goal: 'Переносить размеры вещи в модель без ошибок.',
            theory: [
                'Чтобы деталь подошла к реальной вещи, вещь надо измерить. '
                + 'Звучит очевидно, но именно здесь ошибаются чаще всего.',
                'Меряют по трём направлениям и в самом широком месте: '
                + 'длина, ширина, толщина. По ним и делают посадочное место.',
                'И обязательно запас: телефон в чехле толще на пару '
                + 'миллиметров, плата с проводами — тоже.',
            ],
            steps: [
                'Выбери предмет и измерь его линейкой',
                'Запиши три размера',
                'Добавь запас 2 мм по каждому',
                'Построй под него держатель или подставку',
            ],
            task: 'Подставка под реальный предмет: измерь его сам, добавь '
                + 'запас 2 мм и сделай посадочное место по своим числам.',
            criteria: [
                { kind: 'bodyCount', value: 1, label: 'Одна деталь' },
                { kind: 'feature', value: ['extrude'], label: 'Выдавливание' },
            ],
            freeform: true,
            hints: [
                'Меряй в самом широком месте — иначе предмет не влезет.',
                'Зачем нужен запас, если размер и так измерен точно?',
                'Линейка врёт на полмиллиметра, принтер — ещё на столько же. '
                + 'Запас 2 мм покрывает обе ошибки.',
            ],
        },

        {
            id: 'pr-39-organizer',
            title: 'Органайзер для стола',
            goal: 'Работать с ограничением: уложиться в габарит.',
            theory: [
                'Задание с ограничением: органайзер под свои вещи, но целиком '
                + 'должен влезать в 150 мм. Ограничение меняет всё — придётся '
                + 'выбирать, что важнее.',
                'Сначала список: что должно поместиться. Каждый предмет '
                + 'измеряем, ширины складываем. Не влезло — что-то убираем.',
                'Перегородки между отсеками тоже занимают место: 2 мм '
                + 'на каждую. При пяти отсеках это уже сантиметр.',
            ],
            steps: [
                'Составь список вещей и измерь их',
                'Сложи ширины плюс перегородки',
                'Убедись, что уложился в 150 мм',
                'Построй органайзер с отсеками',
            ],
            task: 'Органайзер для стола: длина не больше 150 мм, минимум три '
                + 'отсека под свои вещи, стенки от 2 мм.',
            criteria: [
                { kind: 'feature', value: ['shell'], label: 'Оболочка' },
                { kind: 'bodyCount', value: 1, label: 'Одна деталь' },
            ],
            freeform: true,
            hints: [
                'Отсеки делают перегородками внутри общей коробки, а не '
                + 'отдельными коробками.',
                'Сколько места съедят перегородки при трёх отсеках?',
                'Две перегородки по 2 мм — это 4 мм от общей длины. '
                + 'Учитывай их сразу.',
            ],
        },

        {
            id: 'pr-40-split',
            title: 'Разборные конструкции',
            goal: 'Делить деталь на части, которые соберутся.',
            theory: [
                'Иногда деталь не влезает на стол принтера или её нельзя '
                + 'напечатать целиком из-за нависаний. Тогда её делят.',
                'Делят там, где стык не мешает: по плоскости, на невидимой '
                + 'стороне, вдали от нагрузки.',
                'Части должны фиксироваться: шип в паз, штифт в отверстие. '
                + 'И между ними — зазор, как во всех сборках.',
            ],
            steps: [
                'Придумай деталь длиннее 200 мм',
                'Раздели её на две части',
                'Сделай шип на одной и паз на другой',
                'Проверь: получилось два тела',
            ],
            task: 'Разборная деталь из двух частей со стыком шип-паз. '
                + 'Зазор в стыке 0,2 мм.',
            criteria: [
                { kind: 'bodyCount', value: 2, label: 'Две части' },
                { kind: 'gap', min: 0.1, max: 0.6, label: 'Зазор в стыке' },
            ],
            freeform: true,
            hints: [
                'Шип и паз — это выступ на одной части и углубление '
                + 'на другой.',
                'Почему шип нельзя сделать ровно по размеру паза?',
                'Не соберётся. Шип делают на 0,2 мм меньше паза — '
                + 'тот же зазор, что и везде.',
            ],
        },

        {
            id: 'pr-41-robot',
            title: 'Корпус робота',
            goal: 'Спроектировать корпус под конкретную начинку.',
            theory: [
                'Задание из робототехники: корпус под плату и мотор. Здесь '
                + 'ничего нельзя придумать — всё диктует начинка.',
                'Порядок: измерь плату и мотор, расставь их мысленно внутри, '
                + 'добавь по 3 мм на стенки и провода — и только тогда '
                + 'задавай габариты корпуса.',
                'Стойки под плату ставят точно под её отверстия. Ошибка '
                + 'в миллиметр — и плата не встанет.',
            ],
            steps: [
                'Измерь плату и мотор',
                'Посчитай габариты корпуса с запасом',
                'Построй коробку и сделай оболочку',
                'Добавь стойки под плату и вырез под вал мотора',
            ],
            task: 'Корпус робота под плату 70 × 50 мм: четыре стойки '
                + 'под её отверстия, вырез под вал мотора, стенки 2 мм.',
            criteria: [
                { kind: 'feature', value: ['shell'], label: 'Оболочка' },
                /* Стойки дают свои цилиндрические грани — числом отверстий
                   такую деталь не проверить надёжно. Смотрим габариты:
                   корпус должен быть построен вокруг платы 70 × 50 мм. */
                { kind: 'size', value: [76, 56, 40], tolerance: 6,
                    label: 'Корпус вокруг платы',
                    hint: 'Плата 70 × 50 мм плюс по 3 мм с каждой стороны — '
                        + 'корпус минимум 76 × 56 мм.' },
                { kind: 'bodyCount', value: 1, label: 'Одна деталь' },
            ],
            hints: [
                'Оболочку делают ДО стоек: иначе она срежет и стойки тоже.',
                'На сколько корпус должен быть больше платы?',
                'Плата 70 × 50, плюс по 3 мм с каждой стороны — корпус '
                + 'минимум 76 × 56 изнутри.',
            ],
        },

        {
            id: 'pr-42-mass',
            title: 'Массогабариты',
            goal: 'Считать вес детали и понимать, зачем это нужно.',
            theory: [
                'У детали есть не только размеры, но и вес. Тяжёлая деталь '
                + 'дольше печатается, больше стоит и хуже работает '
                + 'в движущемся механизме.',
                'Вес считается сам: объём умножить на плотность пластика. '
                + 'В редакторе для этого есть инструмент «Масса и объём».',
                'Сравнивай варианты по весу: одна и та же деталь сплошной '
                + 'и с рёбрами отличается втрое, а держит почти так же.',
            ],
            steps: [
                'Построй деталь 80 × 60 × 20 мм сплошной',
                'Посмотри её массу в анализе',
                'Сделай оболочку 3 мм',
                'Сравни массу до и после',
            ],
            task: 'Деталь 80 × 60 × 20 мм с оболочкой 3 мм. Сравни её массу '
                + 'со сплошным вариантом.',
            blueprint: {
                front: {
                    w: 80,
                    h: 20,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 80, h: 20 },
                        { type: 'line', x1: 3, y1: 0, x2: 3, y2: 17 },
                        { type: 'line', x1: 77, y1: 0, x2: 77, y2: 17 },
                        { type: 'line', x1: 3, y1: 17, x2: 77, y2: 17 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 28, x2: 80, y2: 28, from: 20, label: '80' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 20, from: 0, label: '20' },
                    ]
                },
                side: {
                    w: 60,
                    h: 20,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 60, h: 20 },
                        { type: 'line', x1: 3, y1: 0, x2: 3, y2: 17 },
                        { type: 'line', x1: 57, y1: 0, x2: 57, y2: 17 },
                        { type: 'line', x1: 3, y1: 17, x2: 57, y2: 17 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 28, x2: 60, y2: 28, from: 20, label: '60' },
                        { orient: 'h', x1: 0, y1: 36, x2: 3, y2: 36, from: 60, label: '3' },
                    ]
                },
                top: {
                    w: 80,
                    h: 60,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 80, h: 60 },
                        { type: 'rect', x: 3, y: 3, w: 74, h: 54 },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 68, x2: 80, y2: 68, from: 60, label: '80' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 60, from: 0, label: '60' },
                    ]
                },
                note: 'Та же деталь, что и сплошная 80 × 60 × 20, но с оболочкой '
                    + '3 мм. Сравни объём: сплошная 96 см³, полая — примерно '
                    + 'втрое меньше. Столько же раз отличается и масса.'
            },
            criteria: [
                { kind: 'size', value: [80, 60, 20], tolerance: 0.5,
                    label: 'Габариты' },
                { kind: 'feature', value: ['shell'], label: 'Оболочка' },
                { kind: 'volume', value: 80 * 60 * 20 - 74 * 54 * 17,
                    tolerance: 0.1, label: 'Объём с оболочкой',
                    hint: 'Проверь толщину стенки — нужно 3 мм.' },
            ],
            hints: [
                'Инструмент «Масса и объём» стоит в группе «Анализ».',
                'Во сколько раз уменьшится масса после оболочки?',
                'Примерно втрое: сплошная деталь весит около 119 г, '
                + 'с оболочкой 3 мм — около 40 г.',
            ],
        },

        {
            id: 'pr-43-optimize',
            title: 'Оптимизация',
            goal: 'Убирать материал там, где он не работает.',
            theory: [
                'Убрать материал можно почти везде — вопрос только где. '
                + 'Ошибёшься местом, и деталь сломается в первый же день.',
                'Правило: нагрузка идёт по краям, середина работает мало. '
                + 'Поэтому убирают середину, оставляя рёбра по контуру '
                + 'и крест-накрест.',
                'Нельзя облегчать там, где деталь крепится: вокруг отверстий '
                + 'под винты материал оставляют полностью.',
            ],
            steps: [
                'Построй пластину 100 × 70 × 12 мм',
                'Сделай четыре отверстия под винты по углам',
                'Убери материал в середине, оставив рёбра',
                'Вокруг отверстий материал не трогай',
            ],
            task: 'Облегчённая пластина 100 × 70 × 12 мм: четыре отверстия '
                + 'Ø4,3 мм по углам, середина облегчена, вокруг отверстий '
                + 'материал сохранён.',
            blueprint: {
                front: {
                    w: 100,
                    h: 12,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 100, h: 12 },
                        { type: 'line', x1: 7.85, y1: 0, x2: 7.85, y2: 12, hidden: true },
                        { type: 'line', x1: 12.15, y1: 0, x2: 12.15, y2: 12, hidden: true },
                        { type: 'line', x1: 87.85, y1: 0, x2: 87.85, y2: 12, hidden: true },
                        { type: 'line', x1: 92.15, y1: 0, x2: 92.15, y2: 12, hidden: true },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 20, x2: 100, y2: 20, from: 12, label: '100' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 12, from: 0, label: '12' },
                    ]
                },
                top: {
                    w: 100,
                    h: 70,
                    shapes: [
                        { type: 'rect', x: 0, y: 0, w: 100, h: 70 },
                        { type: 'circle', cx: 10, cy: 10, r: 2.15 },
                        { type: 'circle', cx: 90, cy: 10, r: 2.15 },
                        { type: 'circle', cx: 10, cy: 60, r: 2.15 },
                        { type: 'circle', cx: 90, cy: 60, r: 2.15 },
                        { type: 'rect', x: 22, y: 18, w: 56, h: 34, hidden: true },
                    ],
                    dims: [
                        { orient: 'h', x1: 0, y1: 78, x2: 100, y2: 78, from: 70, label: '100' },
                        { orient: 'v', x1: -8, y1: 0, x2: -8, y2: 70, from: 0, label: '70' },
                        { orient: 'h', x1: 7.85, y1: 86, x2: 12.15, y2: 86, from: 78, label: '⊘4,3' },
                        { orient: 'h', x1: 0, y1: 94, x2: 10, y2: 94, from: 86, label: '10' },
                    ]
                },
                note: 'Четыре отверстия ⊘4,3 мм под М4 по углам — их положение '
                    + 'задано. Штриховая рамка в середине — ЗОНА ОБЛЕГЧЕНИЯ: как '
                    + 'именно её облегчить (окна, рёбра, сетка), решаешь сам. '
                    + 'Вокруг отверстий материал не трогай — там винт прижимает '
                    + 'деталь.'
            },
            criteria: [
                { kind: 'size', value: [100, 70, 12], tolerance: 0.5,
                    label: 'Габариты' },
                { kind: 'holes', value: 4, label: 'Четыре отверстия' },
                { kind: 'bodyCount', value: 1, label: 'Одна деталь' },
            ],
            hints: [
                'Облегчают вырезами или оболочкой — что проще для твоей '
                + 'формы.',
                'Почему вокруг отверстия нельзя убирать материал?',
                'Там винт прижимает деталь: тонкая стенка треснет '
                + 'при затяжке.',
            ],
        },

        {
            id: 'pr-44-export',
            title: 'Экспорт и подготовка к печати',
            goal: 'Отдать модель принтеру правильно.',
            theory: [
                'Модель сохраняют в файл STL: он описывает только форму, '
                + 'без истории построения.',
                'Перед печатью деталь разворачивают. От того, как она лежит '
                + 'на столе, зависит и прочность, и число поддержек: слои '
                + 'держатся друг за друга слабее, чем внутри слоя.',
                'Последняя проверка — та же, что в третьем курсе: толщина '
                + 'стенок и нависания. Две минуты проверки экономят три часа '
                + 'печати в мусор.',
            ],
            steps: [
                'Построй деталь для печати',
                'Проверь толщины стенок',
                'Обойди навигатором в поисках нависаний',
                'Сохрани в STL через меню «Файл»',
            ],
            task: 'Любая деталь, готовая к печати: стенки от 1,5 мм, '
                + 'без нависаний круче 45 градусов.',
            criteria: [
                { kind: 'bodyCount', value: 1, label: 'Одна деталь' },
            ],
            freeform: true,
            hints: [
                'Экспорт STL — в меню «Файл», пункт «Экспорт».',
                'Как повернуть деталь, чтобы обойтись без поддержек?',
                'Положи её так, чтобы все нависания стали наклонными '
                + 'не круче 45 градусов.',
            ],
        },

        {
            id: 'pr-45-final',
            title: 'Итоговый проект',
            goal: 'Сделать свою вещь от требований до готового файла.',
            theory: [
                'Тема свободная, требования обязательные: вещь должна быть '
                + 'нужной, печататься без поддержек и собираться, если '
                + 'деталей несколько.',
                'Порядок работы — весь курс сразу: требования, набросок, '
                + 'измерения, модель, проверка. Ни один шаг не пропускай.',
                'Защита работы — тоже часть задания. Объясни: какую задачу '
                + 'решает вещь, откуда взялся каждый размер и почему деталь '
                + 'сделана именно так.',
            ],
            steps: [
                'Выпиши требования к своей вещи',
                'Сделай набросок и проставь размеры',
                'Построй модель',
                'Проверь по требованиям и подготовь к печати',
            ],
            task: 'Итоговый проект: своя вещь, решающая реальную задачу. '
                + 'Стенки от 1,5 мм, скруглённые края, готово к печати.',
            criteria: [
                { kind: 'feature', value: ['fillet'], label: 'Скруглённые края' },
                { kind: 'bodyCount', value: 1, label: 'Хотя бы одна деталь' },
            ],
            freeform: true,
            hints: [
                'Начни с вопроса: какая вещь тебе самому нужна?',
                'Что должно быть в защите, кроме показа модели?',
                'Объяснение каждого размера: откуда взялось число. '
                + '«Мне так захотелось» — не ответ.',
            ],
        },

        {
            id: 'pr-46-review',
            title: 'Разбор работ',
            goal: 'Смотреть на свою работу со стороны.',
            theory: [
                'Последний урок — про то, как оценивать свою работу. Это '
                + 'отдельный навык, и он важнее, чем кажется.',
                'Первый вопрос: выполнены ли требования? Открой список и '
                + 'пройди по нему пунктами. Не «в целом да», а по каждому.',
                'Второй вопрос: что сделал бы иначе? Обычно находится место, '
                + 'где деталь можно облегчить или собрать из меньшего числа '
                + 'частей. Это и есть рост.',
            ],
            steps: [
                'Открой свой итоговый проект',
                'Пройди по списку требований пунктами',
                'Обойди деталь навигатором со всех сторон',
                'Запиши, что сделал бы иначе',
            ],
            task: 'Возьми свой итоговый проект и сделай вторую версию: '
                + 'легче, проще или удобнее первой.',
            criteria: [
                { kind: 'bodyCount', value: 1, label: 'Одна деталь' },
            ],
            freeform: true,
            hints: [
                'Вторая версия почти всегда лучше первой — это нормально '
                + 'и так работают все инженеры.',
                'Что чаще всего можно улучшить в первой версии?',
                'Вес и число деталей: обычно можно убрать материал '
                + 'и объединить две части в одну.',
            ],
        },
    ],
};


/** Все курсы программы. Пока опубликован первый; остальные — по мере готовности. */
export const COURSES = [COURSE_FIRST_PARTS, COURSE_DRAWINGS,
    COURSE_ASSEMBLY, COURSE_REVOLVE, COURSE_DESIGN];

/** Найти урок по id во всех курсах. */
export function findLesson(lessonId) {
    for (const course of COURSES) {
        const lesson = course.lessons.find((l) => l.id === lessonId);
        if (lesson) return { course, lesson };
    }
    return null;
}

/** Порядковый номер урока внутри курса (для показа «Урок 3 из 8»). */
export function lessonIndex(course, lessonId) {
    return course.lessons.findIndex((l) => l.id === lessonId);
}
